import React, { useState, useEffect } from 'react';
import { ShoppingCart, Plus, Minus, Trash2, Home, Package, User, Search, Bell, MapPin, Menu, Heart, Gift, Phone, MessageSquare, Check, Mail, Chrome, Eye, EyeOff, Camera, Shield, Fingerprint, Key, Smartphone, Clock, Monitor, Activity, AlertTriangle, LogOut, Settings, ChevronRight, Truck, XCircle, CheckCircle, Landmark, Info } from 'lucide-react';
import { products, banners, locations, subscriptionPlans, mockSuggestions, featureSections as sampleFeatureSections } from './data/products';
import { authAPI, productsAPI, cartAPI, ordersAPI, subscriptionsAPI, settingsAPI } from './services/api';
import { featuresAPI } from './services/api';
import './App.css';

import HomeScreen from './components/HomeScreen';
import DesktopHomeScreen from './components/DesktopHomeScreen';
import DesktopNavbar from './components/DesktopNavbar';
import DesktopFooter from './components/DesktopFooter';
import useIsDesktop from './utils/useIsDesktop';
import { filterVisibleCategories } from './utils/categoryConfig';
import CartScreen from './components/CartScreen';
import OrdersScreen from './components/OrdersScreen';
import SubscriptionsScreen from './components/SubscriptionsScreen';
import ProfileScreen from './components/ProfileScreen';
import HelpSupportScreen from './components/HelpSupportScreen';
import AboutUsScreen from './components/AboutUsScreen';
import TermsScreen from './components/TermsScreen';
import PrivacyPolicyScreen from './components/PrivacyPolicyScreen';
import CmsPageScreen from './components/CmsPageScreen';
import SecuritySettingsModal from './components/modals/SecuritySettingsModal';
import TwoFactorSetupModal from './components/modals/TwoFactorSetupModal';
import AuthModal from './components/modals/AuthModal';
import CheckoutModal from './components/modals/CheckoutModal';
import SubscriptionModal from './components/modals/SubscriptionModal';
import LocationModal from './components/modals/LocationModal';
import MyOrdersModal from './components/modals/MyOrdersModal';
import FavoritesModal from './components/modals/FavoritesModal';
import NotificationsModal from './components/modals/NotificationsModal';
import PaymentMethodsModal from './components/modals/PaymentMethodsModal';
import EditAddressModal from './components/modals/EditAddressModal';
import EditProfileModal from './components/modals/EditProfileModal';
import ForgotPasswordModal from './components/modals/ForgotPasswordModal';
import SignUpModal from './components/modals/SignUpModal';
import PaymentModal from './components/modals/PaymentModal';
import OrderConfirmationModal from './components/modals/OrderConfirmationModal';
import LogoutConfirmationModal from './components/modals/LogoutConfirmationModal';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import Toast from './components/Toast';
import Dashboard from './Dashboard';
import PendingWalletTransactions from './admin/PendingWalletTransactions';
import apiService from './apiService';
import AddAddressModal from './components/modals/AddAddressModal';
import WalletTopupModal from './components/modals/WalletTopupModal';
import WalletTransactionsModal from './components/modals/WalletTransactionsModal';
import * as cartHelpers from './utils/cartHelpers';
import { getFinalPrice } from './utils/priceHelpers';
import CookieConsent from './components/CookieConsent';
import CategoryProductsScreen from './components/CategoryProductsScreen';
import OffersScreen from './components/OffersScreen';
import orderHelpers from './utils/orderHelpers';
import * as couponHelpers from './utils/couponHelpers';
import useToast from './hooks/useToast';
import { subscribeToUserNotifications, subscribeToPromotions, subscribeToAppUpdates } from './services/realtimeNotifications';

// ============================================
// Main App Component
const APP_THEME_STORAGE_KEY = 'dealstore_app_theme';
const APP_THEME_META_COLORS = {
  blue: '#2563eb',
  sunrise: '#fff4e0',
  grove: '#f3fbf7',
  midnight: '#0f172a',
  pulse: '#337639',
  white: '#ffffff',
};

const isValidAppTheme = (value) => ['blue', 'sunrise', 'grove', 'midnight', 'pulse', 'white'].includes(value);

const getInitialAppTheme = () => {
  if (typeof window === 'undefined') {
    return 'blue';
  }

  try {
    const storedTheme = window.localStorage.getItem(APP_THEME_STORAGE_KEY);
    return isValidAppTheme(storedTheme) ? storedTheme : 'blue';
  } catch (error) {
    return 'blue';
  }
};

const getInitialAuthBootstrapSnapshot = () => {
  if (typeof window === 'undefined') {
    return {
      hasToken: false,
      shouldDelayAuthUi: false,
    };
  }

  const urlParams = new URLSearchParams(window.location.search);
  const callbackToken =
    urlParams.get('token') ||
    urlParams.get('auth_token') ||
    urlParams.get('access_token');

  const storedToken = apiService.getToken();
  const cachedUser = apiService.getUser();

  return {
    hasToken: !!(storedToken || callbackToken),
    shouldDelayAuthUi: !!(storedToken || callbackToken || cachedUser),
  };
};

const normalizeComparableId = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  return String(value);
};

const toNumericIdIfPossible = (value) => {
  if (value === undefined || value === null || value === '') {
    return value;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
};

const isSameProductId = (left, right) => {
  const leftId = normalizeComparableId(left);
  const rightId = normalizeComparableId(right);

  if (!leftId || !rightId) {
    return false;
  }

  return leftId === rightId;
};

const toFavoriteArrayPayload = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.favorites?.data)) {
    return payload.favorites.data;
  }

  if (Array.isArray(payload?.favorites)) {
    return payload.favorites;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  return [];
};

const normalizeFavorites = (payload) => {
  const rawItems = toFavoriteArrayPayload(payload);
  const dedupedByProductId = new Map();

  rawItems.forEach((entry) => {
    if (!entry || typeof entry !== 'object') {
      return;
    }

    const favoriteRecord = entry.favorite && typeof entry.favorite === 'object' ? entry.favorite : entry;
    const productData = favoriteRecord.product && typeof favoriteRecord.product === 'object'
      ? favoriteRecord.product
      : entry.product && typeof entry.product === 'object'
        ? entry.product
        : favoriteRecord;

    const productIdRaw =
      productData?.id ??
      favoriteRecord?.product_id ??
      favoriteRecord?.productId ??
      entry?.product_id;

    if (productIdRaw === undefined || productIdRaw === null || productIdRaw === '') {
      return;
    }

    const productId = toNumericIdIfPossible(productIdRaw);
    const key = normalizeComparableId(productId);
    if (!key) {
      return;
    }

    const favoriteIdRaw =
      favoriteRecord?.favorite_id ??
      favoriteRecord?.id ??
      entry?.favorite_id ??
      null;

    const normalizedFavorite = {
      ...productData,
      id: productId,
      favorite_id: favoriteIdRaw !== null && favoriteIdRaw !== undefined
        ? toNumericIdIfPossible(favoriteIdRaw)
        : null,
    };

    const existing = dedupedByProductId.get(key);
    if (!existing) {
      dedupedByProductId.set(key, normalizedFavorite);
      return;
    }

    dedupedByProductId.set(key, {
      ...existing,
      ...normalizedFavorite,
      favorite_id: normalizedFavorite.favorite_id || existing.favorite_id || null,
    });
  });

  return Array.from(dedupedByProductId.values());
};

const MilkEcommerceApp = () => {
  
  // Toast notification hook
  const { toast, showToast, hideToast } = useToast();

  // State Management
  const isDesktop = useIsDesktop();
  const [activeTab, setActiveTab] = useState('home');
  const [navHistory, setNavHistory] = useState([]);
  const [cmsPageSlug, setCmsPageSlug] = useState(null);
  const [cmsPageTitle, setCmsPageTitle] = useState('');
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState([]);
  const [categoryPageView, setCategoryPageView] = useState(null);
  const [offersPage, setOffersPage] = useState(null); // null | 'all' | section.id
  const [showCheckout, setShowCheckout] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [showSubscription, setShowSubscription] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [referralInfo, setReferralInfo] = useState(null);
  const [deliverySlot, setDeliverySlot] = useState('morning');
  const [paymentMethod, setPaymentMethod] = useState('upi_cod');
  const [currentBanner, setCurrentBanner] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('Gurugram, Haryana');
  const [initialAuthSnapshot] = useState(() => getInitialAuthBootstrapSnapshot());
  const [isAuthenticated, setIsAuthenticated] = useState(initialAuthSnapshot.hasToken);
  // Guests can browse the home page without logging in; login is prompted only
  // when they open a protected page (cart, orders, subscriptions, profile).
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authBootstrapPending, setAuthBootstrapPending] = useState(initialAuthSnapshot.shouldDelayAuthUi);
  const [authStep, setAuthStep] = useState('phone');
  const [mobile, setMobile] = useState('');
  const [whatsAppNumber, setWhatsAppNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpMethod, setOtpMethod] = useState('sms');
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [userProfile, setUserProfile] = useState({
    name: '',
    email: '',
    address: '',
    mobile: '',
    city: 'Gurgaon',
    pincode: '',
    state: 'Haryana',
    country: 'India',
    profilePic: null,
    whatsappConnected: false,
    is_admin: false
  });

  // Add after userProfile state (around line 62)
  const [deliveryAddresses, setDeliveryAddresses] = useState([
    {
      id: 1,
      label: 'Home',
      longitude: 77.0266
    }
  ]);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null); 
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState(
    () => (Array.isArray(deliveryAddresses) && deliveryAddresses.find(a => a.isDefault)?.id) || deliveryAddresses[0]?.id || null
  );
  const [loginMethod, setLoginMethod] = useState('phone');
  const [password, setPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '', color: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState(['', '', '', '', '', '']);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState(1800);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [showSecuritySettings, setShowSecuritySettings] = useState(false);
  const [loginHistory, setLoginHistory] = useState([
    { id: 1, device: 'Chrome on Windows', location: 'Gurugram, India', ip: '103.xxx.xxx.123', time: '2 hours ago', current: true },
    { id: 2, device: 'Safari on iPhone', location: 'Delhi, India', ip: '103.xxx.xxx.456', time: '1 day ago', current: false },
    { id: 3, device: 'Chrome on Android', location: 'Noida, India', ip: '103.xxx.xxx.789', time: '3 days ago', current: false }
  ]);
  const [securityLogs, setSecurityLogs] = useState([
    { id: 1, action: 'Login Successful', detail: 'Logged in via Email', time: '2 hours ago', type: 'success' },
    { id: 2, action: '2FA Enabled', detail: 'Two-factor authentication activated', time: '2 hours ago', type: 'info' },
    { id: 3, action: 'Profile Updated', detail: 'Changed delivery address', time: '1 day ago', type: 'info' },
    { id: 4, action: 'Failed Login Attempt', detail: 'Invalid password entered', time: '3 days ago', type: 'warning' },
    { id: 5, action: 'Password Changed', detail: 'Password updated successfully', time: '1 week ago', type: 'success' }
  ]);
    
  const [showMyOrders, setShowMyOrders] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [showEditAddress, setShowEditAddress] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [orders, setOrders] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [pendingWalletAmount, setPendingWalletAmount] = useState(0);
  const [payuCallbackResult, setPayuCallbackResult] = useState(null);
  const [showWalletTopup, setShowWalletTopup] = useState(false);
  const [showWalletTransactions, setShowWalletTransactions] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([
 // { id: 1, type: 'UPI', details: 'milkuser@upi', default: true },
 // { id: 2, type: 'Card', details: '**** **** **** 1234', default: false }
]);

  // Settings state
  const [appSettings, setAppSettings] = useState({
    taxRate: 5, // Default 5% GST
    freeShippingThreshold: 500, // Default minimum order for free shipping
    standardShippingFee: 50, // Default shipping fee
    socialMediaLogins: ['google', 'facebook'], // Default enabled social logins
    socialMediaLoginEnabled: true, // Whether social media logins are enabled
    maxCartItems: 20, // Default maximum items allowed in cart,
    defaultDeliverySlot: 'morning', // Default delivery slot,
    supportContact: '+91 88266-00232', // Customer support contact info,
    supportEmail: 'support@dealstore.app , info@algolive.com', // Customer support email,
    corportateAddress: 'ALGOLIVE (OPC) PRIVATE LIMITED, OFFICE 719, 7th floor, SVH,83 Metrostreet, Sector 83, Gurugram, Haryana, 122004', // Corporate office address,
    appVersion: '1.0.0', // App version,
    securityAlertsEnabled: false, // Whether to show security alerts in notifications,
    catalogNamespace: 'milk', // Namespace for product catalog (for future multi-brand support)
    settingsLoaded: false, // Track if settings were loaded from backend
    settingsError: null, // Track any settings loading errors
    appName: 'DealStore App', // App name for display in profile and notifications
    razorpayEnabled: false, // Whether Razorpay payment gateway is enabled
    payuEnabled: true,     // Whether PayU online payments (UPI/Card/Wallet/NetBanking) are enabled
    codEnabled: true,      // Whether Cash on Delivery is enabled
    fssaiLicense: '10826999000444',  // FSSAI food safety license number
    upiId: '',         // Merchant UPI ID for UPI Scan on Delivery
    upiQrCode: '/static/upi-qr.jpeg',  // Merchant UPI QR code image URL
    pmUpiEnabled:        true, // UPI payment method enabled in wallet top-up
    pmCardEnabled:       true, // Card payment method enabled in wallet top-up
    pmNetbankingEnabled: true, // Net Banking enabled in wallet top-up
    pmWalletEnabled:     true, // Mobile Wallet enabled in wallet top-up
    pmStoreQrEnabled:    true, // Store QR (UTR) enabled in wallet top-up
    pwaDownloadUrl:      '', // PWA install or manifest URL
    pwaDownloadEnabled:  true, // Show PWA download/install link
    googlePlayUrl:       '', // Google Play Store URL
    googlePlayEnabled:   true, // Show Google Play download link
    appStoreUrl:         '', // Apple App Store URL
    appStoreEnabled:     true, // Show App Store download link
    razorpay_key:        '', // Razorpay public key
    deliverySlots: [
      { id: 'morning',   label: 'Morning',   time: '6:00 AM – 8:00 AM', icon: '🌅', cutoffHour: 4,  enabled: true },
      { id: 'afternoon', label: 'Afternoon', time: '2:00 PM – 4:00 PM', icon: '☀️', cutoffHour: 12, enabled: true },
      { id: 'evening',   label: 'Evening',   time: '6:00 PM – 8:00 PM', icon: '🌆', cutoffHour: 16, enabled: true },
    ],
  });
  const [appTheme, setAppTheme] = useState(() => getInitialAppTheme());

const [notifications, setNotifications] = useState([]);
const [appUpdateInfo, setAppUpdateInfo] = useState(null);
const [orderToOpenFromNotification, setOrderToOpenFromNotification] = useState(null);

  //const [productsList, setProductsList] = useState(products);
  const [productsList, setProductsList] = useState([]);
  //const [featureSections, setFeatureSections] = useState(sampleFeatureSections);
  const [featureSections, setFeatureSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mobileNotFound, setMobileNotFound] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showAboutPage, setShowAboutPage] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState('');
  const [firstOrderCouponData, setFirstOrderCouponData] = useState(null);

  const syncFavoritesState = (nextFavorites) => {
    const normalized = normalizeFavorites(nextFavorites);
    setFavorites(normalized);
    try {
      localStorage.setItem('favorites', JSON.stringify(normalized));
    } catch (error) {
      console.warn('Failed to cache favorites', error);
    }

    return normalized;
  };
  
  // Effects

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.dataset.theme = appTheme;
    document.documentElement.style.colorScheme = appTheme === 'midnight' ? 'dark' : 'light';

    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', APP_THEME_META_COLORS[appTheme] || APP_THEME_META_COLORS.sunrise);
    }

    try {
      window.localStorage.setItem(APP_THEME_STORAGE_KEY, appTheme);
    } catch (error) {
      // Ignore storage failures and keep the in-memory theme.
    }
  }, [appTheme]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const hasFullPageOverlay =
      showOrderConfirmation ||
      !!showSubscription ||
      showFavorites ||
      !!categoryPageView ||
      !!offersPage ||
      showNotificationsModal ||
      showPaymentMethods ||
      showEditAddress ||
      showAddAddress;

    if (!hasFullPageOverlay) {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [
    showOrderConfirmation,
    showSubscription,
    showFavorites,
    categoryPageView,
    offersPage,
    showNotificationsModal,
    showPaymentMethods,
    showEditAddress,
    showAddAddress,
  ]);

  // Scroll to top whenever the active tab changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeTab]);

  const handleThemeChange = (nextTheme) => {
    if (!isValidAppTheme(nextTheme) || nextTheme === appTheme) {
      return;
    }

    setAppTheme(nextTheme);
    showToast('Theme updated', 'success');
  };

 // Load products on mount (run regardless of auth state)
  useEffect(() => {
    console.log('App mount useEffect running...');
    loadProducts();
    loadSettings();
    apiService.getCategories()
      .then(res => setCategories(filterVisibleCategories(res?.data?.categories || [])))
      .catch(() => {});
    featuresAPI.getActiveSections()
      .then(sections => {
        setFeatureSections(Array.isArray(sections) && sections.length > 0 ? sections : sampleFeatureSections);
      })
      .catch(err => {
        console.error('Failed to load active feature sections:', err);
        setFeatureSections(sampleFeatureSections);
      });

    // â”€â”€ Detect PayU callback return FIRST (before checkAuth) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // PayU does a full-page redirect back here; we must not let checkAuth
    // race with this and accidentally show the login modal.
    const urlParams = new URLSearchParams(window.location.search);
    const callbackToken =
      urlParams.get('token') ||
      urlParams.get('auth_token') ||
      urlParams.get('access_token');

    if (callbackToken) {
      apiService.setToken(callbackToken);
    }

    const paymentStatus = urlParams.get('payment');
    if (paymentStatus) {
      const orderId = urlParams.get('order_id') || urlParams.get('orderId');
      const txnid   = urlParams.get('txnid');
      const reason  = urlParams.get('reason');
      // Clean the URL so refresh doesn't re-trigger
      window.history.replaceState({}, '', window.location.pathname);

      if (paymentStatus === 'success' && apiService.getToken()) {
        // User was already authenticated before redirecting to PayU.
        // Restore session from localStorage immediately â€” skip the async
        // server round-trip (checkAuth) which can race and show the login modal.
        const savedUser = apiService.getUser();
        setAuthenticatedSession(savedUser);
        setAuthBootstrapPending(false);
        if (savedUser) {
          hydrateUserProfile(savedUser);
        }
        loadDeliveryAddresses();

        // Fetch order details and show confirmation modal directly â€” same as COD flow
        apiService.getOrderById(orderId)
          .then(resp => {
            const order = resp?.data?.order || resp?.data || null;
            const deliveryAddr = order ? {
              address: order.shipping_address || '',
              city: order.shipping_city || '',
              state: order.shipping_state || '',
              pincode: order.shipping_pincode || '',
            } : null;
            const rawItems = order?.items || order?.order_items || [];
            const normalizedItems = rawItems.map(item => ({
              ...item,
              name: item.name || item.product_name || item.product?.name || '',
              price: parseFloat(item.price || 0),
              quantity: item.quantity || 1,
            }));
            setOrderDetails({
              orderId: order?.id || orderId,
              orderNumber: order?.order_number || orderId,
              paymentTxnId: txnid,
              items: normalizedItems,
              totalAmount: order?.total || 0,
              originalAmount: order?.subtotal || order?.total || 0,
              couponCode: order?.coupon_code || null,
              couponDiscount: order?.coupon_discount || 0,
              shippingCharge: order?.shipping_fee || 0,
              taxAmount: order?.tax || 0,
              deliverySlot: order?.delivery_slot || 'morning',
              paymentMethod: order?.payment_method || 'upi',
              deliveryAddress: order?.delivery_address || deliveryAddr,
              estimatedDelivery: 'Tomorrow, ' + new Date(Date.now() + 86400000).toLocaleDateString('en-IN', {
                weekday: 'short', month: 'short', day: 'numeric'
              })
            });
            setShowOrderConfirmation(true);
            loadOrders(1);
            cartHelpers.clearCart().catch(() => {});
            setCart([]);
            showToast('Payment successful! Your order has been confirmed.', 'success');
          })
          .catch(err => {
            console.error('Failed to fetch order after PayU success:', err);
            // Still show confirmation even if order fetch fails
            setOrderDetails({
              orderId: orderId,
              orderNumber: orderId,
              paymentTxnId: txnid,
              items: [],
              totalAmount: 0,
              paymentMethod: 'upi',
              estimatedDelivery: 'Tomorrow, ' + new Date(Date.now() + 86400000).toLocaleDateString('en-IN', {
                weekday: 'short', month: 'short', day: 'numeric'
              })
            });
            setShowOrderConfirmation(true);
            showToast('Payment successful! Your order has been confirmed.', 'success');
            cartHelpers.clearCart().catch(() => {});
            setCart([]);
          });

        // Skip checkAuth â€” token is trusted for the payment return flow
        return;
      }

      // Defer remaining callback handling to dedicated effect and avoid
      // checkAuth race that can show login modal before confirmation.
      setShowAuthModal(false);
      setAuthBootstrapPending(false);
      setPayuCallbackResult({ status: paymentStatus, orderId, txnid, reason });
      return;
    }

    if (callbackToken) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Normal app load â€” validate session with server
    checkAuth();
  }, []);

  // Load cart only after authentication
  useEffect(() => {
    if (isAuthenticated) {
      console.log('User authenticated, loading cart...');
      // loadCart();
    }
  }, [isAuthenticated]);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const hydrateUserProfile = (user) => {
    if (!user || typeof user !== 'object') {
      return;
    }

    setUserProfile({
      name: user.name || '',
      email: user.email || '',
      mobile: user.mobile || '',
      address: user.address || '',
      city: user.city || 'Gurgaon',
      state: user.state || 'Haryana',
      pincode: user.pincode || '',
      country: user.country || 'India',
      profilePic: user.profilePic || null,
      is_admin: user.is_admin || false,
      email_verified_at: user.email_verified_at || null,
      profile_completed: Boolean(user.profile_completed),
    });
    setMobile(user.mobile || '');
  };

  const setAuthenticatedSession = (user = null) => {
    setIsAuthenticated(true);
    setShowAuthModal(false);

    if (user && typeof user === 'object') {
      hydrateUserProfile(user);
      apiService.setUser(user);
    }
  };

  const setUnauthenticatedSession = () => {
    // Drop to guest mode (home stays browsable). Login is prompted on demand
    // when the user opens a protected page or taps Login.
    setIsAuthenticated(false);
  };

  useEffect(() => {
    const handleUnauthorized = () => {
      apiService.removeToken({ clearAppState: true });
      setUnauthenticatedSession();
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  // Tabs that require the user to be logged in
  const PROTECTED_TABS = ['cart', 'orders', 'subscriptions', 'profile'];

  const navigate = (tab) => {
    // Guests must log in before opening a protected page
    if (PROTECTED_TABS.includes(tab) && !isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    // Always dismiss overlay screens when switching tabs
    setCategoryPageView(null);
    setOffersPage(null);
    setShowSubscription(null);
    setEditingSubscription(null);
    setShowFavorites(false);
    setShowNotificationsModal(false);
    setShowOrderConfirmation(false);
    setShowCheckout(false);
    if (tab === activeTab) return;
    setNavHistory(prev => [...prev, activeTab]);
    setActiveTab(tab);
  };

  // Notifications and Favorites also require login — prompt guests instead of opening.
  const openNotifications = () => {
    if (!isAuthenticated) { setShowAuthModal(true); return; }
    setShowNotificationsModal(true);
  };
  const openFavorites = () => {
    if (!isAuthenticated) { setShowAuthModal(true); return; }
    setShowFavorites(true);
  };
  // Subscribe flow also requires login (used by the home Subscribe pill and
  // the per-product "Subscribe & Save" buttons).
  const openSubscription = (arg) => {
    if (!isAuthenticated) { setShowAuthModal(true); return; }
    setShowSubscription(arg);
  };

  const goBack = () => {
    if (navHistory.length > 0) {
      const prev = navHistory[navHistory.length - 1];
      setNavHistory(h => h.slice(0, -1));
      setActiveTab(prev);
    } else {
      setActiveTab('home');
    }
  };

  // Lock the underlying page scroll while the full-screen About overlay is open,
  // so only the overlay shows a scrollbar (avoids a second background scrollbar).
  useEffect(() => {
    if (!showAboutPage) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [showAboutPage]);

  const onRegistrationSuccess = (user) => {
    if (user) {
      hydrateUserProfile(user);
      apiService.setUser(user);
      setMobile(user.mobile || '');
    }
    setShowSignUp(false);

    // Registration now collects everything (name, mobile, address, WhatsApp,
    // email/password) and verifies OTP up front — so go straight to the app.
    // No "Complete Your Profile" / WhatsApp intermediate screens.
    setIsAuthenticated(true);
    setShowAuthModal(false);
    setAuthStep('completed');
    setActiveTab('home');
    window.scrollTo({ top: 0, behavior: 'instant' });
    try { loadFavorites(); } catch (_) {}
  };

  const persistAuthPayload = (payload, source) => {
    const token = payload?.token || payload?.auth_token || payload?.access_token || null;
    const user = payload?.user || payload?.data?.user || null;

    if (!token) {
      throw new Error(`Missing authentication token in ${source}`);
    }

    apiService.setToken(token);
    if (user && typeof user === 'object') {
      apiService.setUser(user);
    }

    return { token, user };
  };

  const checkAuth = async () => {
    const token = apiService.getToken();
    const savedUser = apiService.getUser();

    if (!token) {
      setUnauthenticatedSession();
      setAuthBootstrapPending(false);
      return;
    }

    // Any token means we optimistically keep app state; backend validation runs in background.
    setAuthenticatedSession(savedUser);

    try {
      const response = await apiService.getAuthenticatedUser({ logoutOnUnauthorized: false });
      if (response && response.success && response.data?.user) {
        const user = response.data.user;
        setAuthenticatedSession(user);
        console.log('Session restored:', user.name);
      } else if (response?.status === 401 || response?.status === 403) {
        // Avoid hard logout loops on refresh; keep token-based optimistic session.
        console.warn('Unauthorized during startup validation; keeping token session and retrying on next protected call.');
        setAuthenticatedSession(savedUser);
      }
    } catch (error) {
      console.error('Session validation failed (non-blocking):', error);
      // Keep optimistic session on network/server issues to avoid reload->login loops.
    } finally {
      setAuthBootstrapPending(false);
    }
  };

  // Pause subscription via API (falls back to subscriptionsAPI)
  const pauseSubscription = async (subscriptionId, pauseUntil = null) => {
    if (!subscriptionId) {
      showToast && showToast('No subscription selected', 'warning');
      return;
    }

    try {
      setLoading(true);
      let resp;
      if (apiService && typeof apiService.pauseSubscription === 'function') {
        resp = await apiService.pauseSubscription(subscriptionId, pauseUntil);
      } else if (subscriptionsAPI && typeof subscriptionsAPI.pause === 'function') {
        resp = await subscriptionsAPI.pause(subscriptionId);
      }

      if (resp && resp.success) {
        const updated = resp.data?.subscription || { ...(subscription || {}), id: subscriptionId, status: resp.data?.status || 'paused' };
        setSubscription(updated);
        localStorage.setItem('subscription', JSON.stringify(updated));
        showToast && showToast('Subscription paused', 'success');
      } else {
        showToast && showToast(resp?.data?.message || 'Failed to pause subscription', 'error');
      }
    } catch (err) {
      console.error('Error pausing subscription:', err);
      showToast && showToast('Error pausing subscription', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Resume subscription via API
  const resumeSubscription = async (subscriptionId) => {
    if (!subscriptionId) {
      showToast && showToast('No subscription selected', 'warning');
      return;
    }

    try {
      setLoading(true);
      let resp;
      if (apiService && typeof apiService.resumeSubscription === 'function') {
        resp = await apiService.resumeSubscription(subscriptionId);
      } else if (subscriptionsAPI && typeof subscriptionsAPI.resume === 'function') {
        resp = await subscriptionsAPI.resume(subscriptionId);
      }

      if (resp && resp.success) {
        const updated = resp.data?.subscription || { ...(subscription || {}), id: subscriptionId, status: resp.data?.status || 'active' };
        setSubscription(updated);
        localStorage.setItem('subscription', JSON.stringify(updated));
        showToast && showToast('Subscription resumed', 'success');
      } else {
        showToast && showToast(resp?.data?.message || 'Failed to resume subscription', 'error');
      }
    } catch (err) {
      console.error('Error resuming subscription:', err);
      showToast && showToast('Error resuming subscription', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Cancel subscription via API
  const cancelSubscription = async (subscriptionId, reason = '') => {
    if (!subscriptionId) {
      showToast && showToast('No subscription selected', 'warning');
      return;
    }

    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;

    try {
      setLoading(true);
      let resp;
      if (apiService && typeof apiService.cancelSubscription === 'function') {
        resp = await apiService.cancelSubscription(subscriptionId, reason);
      } else if (subscriptionsAPI && typeof subscriptionsAPI.cancel === 'function') {
        resp = await subscriptionsAPI.cancel(subscriptionId, reason);
      }

      if (resp && resp.success) {
        const updated = resp.data?.subscription || { ...(subscription || {}), id: subscriptionId, status: resp.data?.status || 'cancelled' };
        setSubscription(updated);
        localStorage.setItem('subscription', JSON.stringify(updated));
        showToast && showToast('Subscription cancelled', 'success');
      } else {
        showToast && showToast(resp?.data?.message || 'Failed to cancel subscription', 'error');
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      showToast && showToast('Error cancelling subscription', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Reactivate a cancelled subscription
  const reactivateSubscription = async (subscriptionId) => {
    if (!subscriptionId) {
      showToast && showToast('No subscription selected', 'warning');
      return;
    }

    try {
      setLoading(true);
      let resp;
      if (apiService && typeof apiService.reactivateSubscription === 'function') {
        resp = await apiService.reactivateSubscription(subscriptionId);
      } else if (subscriptionsAPI && typeof subscriptionsAPI.reactivate === 'function') {
        resp = await subscriptionsAPI.reactivate(subscriptionId);
      } else {
        // Fallback to resume endpoint if reactivate not available
        resp = await apiService.resumeSubscription(subscriptionId);
      }

      if (resp && resp.success) {
        const updated = resp.data?.subscription || { ...(subscription || {}), id: subscriptionId, status: resp.data?.status || 'active' };
        setSubscription(updated);
        localStorage.setItem('subscription', JSON.stringify(updated));
        showToast && showToast('Subscription reactivated', 'success');
      } else {
        showToast && showToast(resp?.data?.message || 'Failed to reactivate subscription', 'error');
      }
    } catch (err) {
      console.error('Error reactivating subscription:', err);
      showToast && showToast('Error reactivating subscription', 'error');
    } finally {
      setLoading(false);
    }
  };

  async function loadProducts() {
    try {
      setLoading(true);
      console.log('loadProducts: starting...');
      console.log('API baseURL=', apiService.baseURL);
      console.log('REACT_APP_API_URL env=', process.env.REACT_APP_API_URL);
      // call API
      const resp = await apiService.getProducts();
      console.log('loadProducts: api response:', resp);

      // Normalize various possible response shapes
      let productsData = [];
      if (!resp) {
        productsData = [];
      } else if (Array.isArray(resp)) {
        productsData = resp;
      } else if (resp.success && resp.data) {
        // Handle paginated response: resp.data.products.data
        if (resp.data.products && Array.isArray(resp.data.products.data)) {
          productsData = resp.data.products.data;
        } else if (Array.isArray(resp.data)) {
          productsData = resp.data;
        } else if (Array.isArray(resp.data.products)) {
          productsData = resp.data.products;
        } else if (Array.isArray(resp.data.items)) {
          productsData = resp.data.items;
        } else if (Array.isArray(resp.data.data)) {
          productsData = resp.data.data;
        } else {
          productsData = resp.data.products || resp.data.items || [];
        }
      } else if (resp.data && resp.data.products && Array.isArray(resp.data.products.data)) {
        // Direct access to paginated structure
        productsData = resp.data.products.data;
      } else if (resp.data && Array.isArray(resp.data.products)) {
        productsData = resp.data.products;
      } else if (Array.isArray(resp.products)) {
        productsData = resp.products;
      }

      console.log('loadProducts: parsed products count =', productsData.length);

      // Use API data if available, otherwise keep existing static products
      setProductsList(Array.isArray(productsData) && productsData.length > 0 ? productsData : products);
    } catch (err) {
      setError('Failed to load products');
      console.error(err);
      // Fallback to static products data instead of empty array
      setProductsList(products);
    } finally {
      setLoading(false);
    }
  }

  const loadSettings = async () => {
    try {
      console.log('Loading settings from backend...');
      const response = await settingsAPI.getSettings();
      console.log('Settings API response:', response);
      
      // Extract settings from response (check multiple possible structures)
      const settings = response?.data || response?.settings || response;
      
      if (settings && typeof settings === 'object') {
        console.log('Parsing settings data:', settings);
        
        // List all expected settings
        const expectedSettings = [
          'taxRate', 'tax_rate',
          'freeShippingThreshold', 'free_shipping_threshold', 
          'standardShippingFee', 'standard_shipping_fee',
          'socialMediaLoginEnabled', 'social_media_login_enabled',
          'maxCartItems', 'max_cart_items',
          'defaultDeliverySlot', 'default_delivery_slot',
          'supportContact', 'support_contact',
          'supportEmail', 'support_email',
          'appVersion', 'app_version',
          'securityAlertsEnabled', 'security_alerts_enabled', 'security_alerts',
          'catalogNamespace', 'catalog_namespace',
          'appName', 'app_name',
          'pwaDownloadUrl', 'pwa_download_url',
          'pwaDownloadEnabled', 'pwa_download_enabled',
          'googlePlayUrl', 'google_play_url',
          'googlePlayEnabled', 'google_play_enabled',
          'appStoreUrl', 'app_store_url',
          'appStoreEnabled', 'app_store_enabled'
        ];
        
        console.log('Expected settings keys:', expectedSettings);
        console.log('Received settings keys:', Object.keys(settings));
        
        // Check which expected settings are missing
        const missingSettings = expectedSettings.filter(key => !(key in settings));
        const foundSettings = expectedSettings.filter(key => key in settings);
        
        console.log('Found settings:', foundSettings);
        console.log('Missing settings:', missingSettings);
        
        // Parse and convert values, handling strings and numbers
        const newSettings = {
          taxRate: parseFloat(settings.tax_rate || settings.taxRate || appSettings.taxRate),
          freeShippingThreshold: parseInt(settings.free_shipping_threshold || settings.freeShippingThreshold || appSettings.freeShippingThreshold, 10),
          standardShippingFee: parseInt(settings.standard_shipping_fee || settings.standardShippingFee || appSettings.standardShippingFee, 10),
          socialMediaLoginEnabled: settings.social_media_login_enabled !== undefined ? settings.social_media_login_enabled : appSettings.socialMediaLoginEnabled,
          maxCartItems: parseInt(settings.max_cart_items || settings.maxCartItems || appSettings.maxCartItems, 10),
          defaultDeliverySlot: settings.default_delivery_slot || settings.defaultDeliverySlot || appSettings.defaultDeliverySlot,
          supportContact: settings.support_contact || settings.supportContact || appSettings.supportContact,
          supportEmail: settings.support_email || settings.supportEmail || appSettings.supportEmail,
          appVersion: settings.app_version || settings.appVersion || appSettings.appVersion,
          securityAlertsEnabled: settings.security_alerts_enabled !== undefined ? settings.security_alerts_enabled : (settings.security_alerts === true ? true : appSettings.securityAlertsEnabled),
          catalogNamespace: settings.catalog_namespace || settings.catalogNamespace || appSettings.catalogNamespace,
          appName: settings.app_name || settings.appName || appSettings.appName,
          razorpayEnabled: settings.razorpay_enabled !== undefined ? !!settings.razorpay_enabled : appSettings.razorpayEnabled,
          payuEnabled:     settings.payu_enabled     !== undefined ? !!settings.payu_enabled     : appSettings.payuEnabled,
          codEnabled:      settings.cod_enabled      !== undefined ? !!settings.cod_enabled      : appSettings.codEnabled,
          fssaiLicense: settings.fssai_license || settings.fssaiLicense || appSettings.fssaiLicense,
          upiId: settings.upi_id ?? appSettings.upiId,
          upiQrCode: settings.upi_qr_code || appSettings.upiQrCode,
          pmUpiEnabled:        settings.pm_upi_enabled        !== undefined ? !!settings.pm_upi_enabled        : appSettings.pmUpiEnabled,
          pmCardEnabled:       settings.pm_card_enabled       !== undefined ? !!settings.pm_card_enabled       : appSettings.pmCardEnabled,
          pmNetbankingEnabled: settings.pm_netbanking_enabled !== undefined ? !!settings.pm_netbanking_enabled : appSettings.pmNetbankingEnabled,
          pmWalletEnabled:     settings.pm_wallet_enabled     !== undefined ? !!settings.pm_wallet_enabled     : appSettings.pmWalletEnabled,
          pmStoreQrEnabled:    settings.pm_store_qr_enabled   !== undefined ? !!settings.pm_store_qr_enabled   : appSettings.pmStoreQrEnabled,
          pwaDownloadUrl:      settings.pwa_download_url || settings.pwaDownloadUrl || appSettings.pwaDownloadUrl,
          pwaDownloadEnabled:  settings.pwa_download_enabled !== undefined ? !!settings.pwa_download_enabled : appSettings.pwaDownloadEnabled,
          googlePlayUrl:       settings.google_play_url || settings.googlePlayUrl || appSettings.googlePlayUrl,
          googlePlayEnabled:   settings.google_play_enabled !== undefined ? !!settings.google_play_enabled : appSettings.googlePlayEnabled,
          appStoreUrl:         settings.app_store_url || settings.appStoreUrl || appSettings.appStoreUrl,
          appStoreEnabled:     settings.app_store_enabled !== undefined ? !!settings.app_store_enabled : appSettings.appStoreEnabled,
          razorpay_key: settings.razorpay_key || appSettings.razorpay_key || '',
          deliverySlots: (() => {
            let raw = settings.delivery_slots;
            // Unwrap any legacy double-encoded JSON strings
            while (typeof raw === 'string') {
              try { raw = JSON.parse(raw); } catch (_) { raw = null; break; }
            }
            return Array.isArray(raw) && raw.length > 0 ? raw : appSettings.deliverySlots;
          })(),
        };
        
        console.log('Updated settings:', newSettings);
        console.log('Settings comparison:');
        Object.keys(newSettings).forEach(key => {
          const oldValue = appSettings[key];
          const newValue = newSettings[key];
          if (oldValue !== newValue) {
            console.log(`  ${key}: ${oldValue} â†’ ${newValue}`);
          } else {
            console.log(`  ${key}: ${oldValue} (unchanged)`);
          }
        });
        
        setAppSettings(prev => ({ ...prev, ...newSettings, settingsLoaded: true, settingsError: null }));
        console.log('Settings loaded from server', 'success');
      } else {
        console.log('No valid settings data received, using defaults');
        console.log('Response structure analysis:', {
          hasData: !!response?.data,
          hasSettings: !!response?.settings,
          isDirectObject: typeof response === 'object',
          responseKeys: response ? Object.keys(response) : []
        });
        setAppSettings(prev => ({ ...prev, settingsLoaded: false, settingsError: 'No valid settings data received' }));
      }
    } catch (err) {
      console.error('Failed to load settings from backend:', err.message);
      console.error('Error details:', err);
      // Try alternative endpoint using settingsAPI
      try {
        console.log('Trying alternative settings endpoint via settingsAPI.getAppConfig...');
        const altResponse = await settingsAPI.getAppConfig();
        console.log('Alternative settings response:', altResponse);
        
        // Extract settings from alternative response
        const settingsData = altResponse?.data || altResponse?.config || altResponse;
        
        if (settingsData && typeof settingsData === 'object') {
          const newSettings = {
            taxRate: parseFloat(settingsData.tax_rate || settingsData.taxRate || appSettings.taxRate),
            freeShippingThreshold: parseInt(settingsData.free_shipping_threshold || settingsData.freeShippingThreshold || appSettings.freeShippingThreshold, 10),
            standardShippingFee: parseInt(settingsData.standard_shipping_fee || settingsData.standardShippingFee || appSettings.standardShippingFee, 10),
            socialMediaLoginEnabled: settingsData.social_media_login_enabled !== undefined ? settingsData.social_media_login_enabled : appSettings.socialMediaLoginEnabled,
            maxCartItems: parseInt(settingsData.max_cart_items || settingsData.maxCartItems || appSettings.maxCartItems, 10),
            defaultDeliverySlot: settingsData.default_delivery_slot || settingsData.defaultDeliverySlot || appSettings.defaultDeliverySlot,
            supportContact: settingsData.support_contact || settingsData.supportContact || appSettings.supportContact,
            supportEmail: settingsData.support_email || settingsData.supportEmail || appSettings.supportEmail,
            appVersion: settingsData.version || settingsData.app_version || settingsData.appVersion || appSettings.appVersion,
            securityAlertsEnabled: settingsData.securityAlertsEnabled === 'true' || settingsData.security_alerts === true || appSettings.securityAlertsEnabled,
            catalogNamespace: settingsData.catalog_namespace || settingsData.catalogNamespace || appSettings.catalogNamespace,
            fssaiLicense: settingsData.fssai_license || settingsData.fssaiLicense || appSettings.fssaiLicense,
            payuEnabled: settingsData.payu_enabled !== undefined ? !!settingsData.payu_enabled : appSettings.payuEnabled,
            codEnabled:  settingsData.cod_enabled  !== undefined ? !!settingsData.cod_enabled  : appSettings.codEnabled,
            upiId: settingsData.upi_id ?? appSettings.upiId,
            upiQrCode: settingsData.upi_qr_code || appSettings.upiQrCode,
            pwaDownloadUrl: settingsData.pwa_download_url || settingsData.pwaDownloadUrl || appSettings.pwaDownloadUrl,
            pwaDownloadEnabled: settingsData.pwa_download_enabled !== undefined ? !!settingsData.pwa_download_enabled : appSettings.pwaDownloadEnabled,
            googlePlayUrl: settingsData.google_play_url || settingsData.googlePlayUrl || appSettings.googlePlayUrl,
            googlePlayEnabled: settingsData.google_play_enabled !== undefined ? !!settingsData.google_play_enabled : appSettings.googlePlayEnabled,
            appStoreUrl: settingsData.app_store_url || settingsData.appStoreUrl || appSettings.appStoreUrl,
            appStoreEnabled: settingsData.app_store_enabled !== undefined ? !!settingsData.app_store_enabled : appSettings.appStoreEnabled,
            deliverySlots: Array.isArray(settingsData.delivery_slots) && settingsData.delivery_slots.length > 0
              ? settingsData.delivery_slots
              : appSettings.deliverySlots,
            defaultDeliverySlot: settingsData.default_delivery_slot || settingsData.defaultDeliverySlot || appSettings.defaultDeliverySlot,
          };
          
          setAppSettings(prev => ({ ...prev, ...newSettings, settingsLoaded: true, settingsError: null }));
          console.log('Alternative settings loaded successfully:', newSettings);
          showToast('Settings loaded from alternative endpoint', 'info');
        } else {
          console.log('No valid data from alternative endpoint');
          setAppSettings(prev => ({ ...prev, settingsLoaded: false, settingsError: `Settings loading failed: ${err.message}` }));
          showToast('Using default settings (server unavailable)', 'warning');
        }
      } catch (altErr) {
        console.error('Alternative settings endpoint also failed:', altErr.message);
        console.log('Using default settings values');
        setAppSettings(prev => ({ ...prev, settingsLoaded: false, settingsError: `Settings loading failed: ${err.message}` }));
        showToast('Using default settings (server unavailable)', 'warning');
      }
    }
  };

  // Manual refresh settings function
  const refreshSettings = async () => {
    console.log('=== MANUAL SETTINGS REFRESH ===');
    showToast('Refreshing settings...', 'info');
    await loadSettings();
  };

  // Test function to manually check settings API (exposed to window for console testing)
  const testSettingsAPI = async () => {
    console.log('=== TESTING SETTINGS API ===');
    
    try {
      console.log('Testing settingsAPI.getSettings()...');
      const response1 = await settingsAPI.getSettings();
      console.log('getSettings() response:', response1);
    } catch (error1) {
      console.error('getSettings() failed:', error1);
    }
    
    try {
      console.log('Testing settingsAPI.getAppConfig()...');
      const response2 = await settingsAPI.getAppConfig();
      console.log('getAppConfig() response:', response2);
    } catch (error2) {
      console.error('getAppConfig() failed:', error2);
    }
    
    try {
      console.log('Testing direct API call to /settings...');
      const response3 = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/settings`);
      const data3 = await response3.json();
      console.log('Direct /settings response:', { status: response3.status, data: data3 });
    } catch (error3) {
      console.error('Direct API call failed:', error3);
    }
  };

  // Expose test function to window for console access
  React.useEffect(() => {
    window.testSettingsAPI = testSettingsAPI;
    window.refreshSettings = refreshSettings;
    window.currentAppSettings = appSettings;
    window.setActiveTab = setActiveTab;
    window.reloadCart = loadCart;
    window.openCmsPage = (slug, title = '') => {
      setCmsPageSlug(slug);
      setCmsPageTitle(title);
      setActiveTab('cms-page');
    };
    return () => {
      delete window.testSettingsAPI;
      delete window.refreshSettings;
      delete window.currentAppSettings;
      delete window.setActiveTab;
      delete window.reloadCart;
      delete window.openCmsPage;
    };
  }, [appSettings, setActiveTab]);

  const loadCart_old = () => {
    const cartData = cartAPI.get();
    setCart(cartData);
  };

 const loadCart = async () => {
  try {
    const cartData = await cartHelpers.loadCart();
  //  alert("Loaded Cart Data:"+JSON.stringify(cartData));
    // Ensure it's always an array
    setCart(Array.isArray(cartData) ? cartData : []);
  } catch (error) {
    console.error('Error loading cart:', error);
    // Fallback to empty cart
    setCart([]);
  }
};
  const handleLogin = async (email, password) => {
    try {
      setLoading(true);
      const data = await authAPI.login(email, password);
      setIsAuthenticated(true);
      try { await loadFavorites(); } catch(e) { console.warn('loadFavorites failed after handleLogin', e); }
      console.log(`Welcome ${data.user.name}!`);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTPForLogin = async (mobile, password, method) => {
  try {
    setLoading(true);
    const response = await apiService.loginWithMobile({
      mobile: mobile,
   //   password: password,
      remember: true
    });

    if (response.success) {
      // OTP sent successfully - do not authenticate yet
      console.log('OTP sent successfully for mobile login');
      setMobileNotFound(false);
      return { success: true, data: response, deliveryMethod: response.data?.delivery_method }; // Return success
    } else {
      // The mobile field error (e.g. "No account found") lives in errors.mobile[0];
      // response.data.message is just "Validation failed" for 422s.
      console.log('[OTP debug] response.status:', response.status, 'response.data:', JSON.stringify(response.data));
      const mobileFieldError = response.data?.errors?.mobile?.[0] || '';
      let errorMessage = mobileFieldError || response.data?.message || 'Failed to send OTP';
      let notFound = false;

      if (mobileFieldError.toLowerCase().includes('no account') ||
          mobileFieldError.toLowerCase().includes('not found')) {
        errorMessage = 'No account found with this mobile number';
        notFound = true;
      } else if (errorMessage.toLowerCase().includes('not found') ||
          errorMessage.toLowerCase().includes('no account') ||
          errorMessage.toLowerCase().includes('user not found') ||
          errorMessage.toLowerCase().includes('mobile not found')) {
        errorMessage = 'No account found with this mobile number';
        notFound = true;
      } else if (errorMessage.toLowerCase().includes('validation failed') ||
          errorMessage.toLowerCase().includes('validation error')) {
        errorMessage = 'Please check your mobile number and try again';
      } else if (errorMessage.toLowerCase().includes('invalid') ||
                 errorMessage.toLowerCase().includes('unprocessable')) {
        errorMessage = 'Invalid mobile number format';
      }

      setMobileNotFound(notFound);
      console.log('Send OTP failed: ' + errorMessage);
      return { success: false, error: errorMessage }; // Return failure
    }
  } catch (err) {
    let errorMessage = err.message || 'An error occurred';

    // Handle HTTP errors
    if (err.response) {
      const status = err.response.status;
      if (status === 404) {
        errorMessage = 'No account found with this mobile number';
        setMobileNotFound(true);
      } else if (status === 400) {
        errorMessage = 'Invalid mobile number format';
      } else if (status >= 500) {
        errorMessage = 'Server error. Please try again later';
      }
    }
    
    // Filter out generic validation messages
    if (errorMessage.toLowerCase().includes('no account') ||
        errorMessage.toLowerCase().includes('not found')) {
      errorMessage = 'No account found with this mobile number';
      setMobileNotFound(true);
    } else if (errorMessage.toLowerCase().includes('validation failed') ||
        errorMessage.toLowerCase().includes('validation error')) {
      errorMessage = 'Please check your mobile number and try again';
    }

    console.log('Error: ' + errorMessage);
    return { success: false, error: errorMessage }; // Return error
  } finally {
    setLoading(false);
  }
};


  const handleSendOTP = async (mobile, method) => {
    try {
      setLoading(true);
     // await authAPI.sendOTP(mobile, method);
      const response = await apiService.sendOTPForLogin(mobile);
      console.log(`OTP sent via ${method}`);
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (mobile, otp) => {
    try {
      setLoading(true);
      //const data = await authAPI.verifyOTP(mobile, otp);
      const response = await apiService.verifyMobileOtp(mobile, otp);
      setIsAuthenticated(true);
      try { await loadFavorites(); } catch(e) { console.warn('loadFavorites failed after handleVerifyOTP', e); }
      console.log('Login successful!');
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart_old = (product) => {
    const updatedCart = cartAPI.add(product);
    setCart(updatedCart);
    console.log('Cart after updateQuantity:', updatedCart);
    console.log(`${product.name} added to cart!`);
  };
  
  const handleAddToCart = async (product) => {
    try {
      const updatedCart = await cartHelpers.addToCart(cart, product);
      setCart(updatedCart);
      console.log(`${product.name} added to cart!`);
    } catch (error) {
      console.error('Error adding to cart:', error);
      console.log('Failed to add to cart. Please try again.');
    }
  };
  const handleUpdateQuantity_old = (productId, quantity) => {
    const updatedCart = cartAPI.updateQuantity(productId, quantity);
    setCart(updatedCart);
  };

  const handleRemoveFromCart_old = (productId) => {
    const updatedCart = cartAPI.remove(productId);
    setCart(updatedCart);
  };

  const handleUpdateQuantity = async (productId, quantity) => {
    try {
      // Calculate delta from current quantity
      const item = cart.find(i => i.id === productId);
      if (item) {
        const delta = quantity - item.quantity;
        const updatedCart = await cartHelpers.updateQuantity(cart, productId, delta);
        setCart(updatedCart);
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      console.log('Failed to update quantity. Please try again.');
    }
  };

  const handleRemoveFromCart = async (productId) => {
    try {
      const updatedCart = await cartHelpers.removeFromCart(cart, productId);
      setCart(updatedCart);
    } catch (error) {
      console.error('Error removing from cart:', error);
      console.log('Failed to remove item. Please try again.');
    }
  };

  const handleCheckout = async (orderData) => {
    try {
      setLoading(true);
      const order = await ordersAPI.create(orderData);
      cartAPI.clear();
      setCart([]);
      console.log('Order placed successfully!');
      return order;
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Open checkout modal
  const openCheckout = () => {
    if (cart.length === 0) {
      showToast('Your cart is empty. Please add items to checkout.', 'warning');
      return;
    }
    if (!isAuthenticated) {
      showToast('Please log in to continue to checkout.', 'info');
      setShowAuthModal(true);
      return;
    }
    setShowCheckout(true);
  };

  const handleCheckout_new = async (orderData) => {
    try {
      setLoading(true);
      const order = await ordersAPI.create(orderData);
      
      // Clear cart using helper
      await clearCart();
      setCart([]);
      
      console.log('Order placed successfully!');
      return order;
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Coupon handlers
  const handleApplyCoupon = async (overrideCode) => {
    setCouponError('');
    const codeToUse = (overrideCode || couponCode || '').trim();

    if (!codeToUse) {
      setCouponError('Please enter a coupon code');
      return;
    }

    const cartTotal = cartHelpers.getCartTotal(cart);
    const isFirstOrder = orders.length === 0;

    const result = couponHelpers.validateCoupon(codeToUse, cartTotal, isFirstOrder);

    if (result.valid) {
      setCouponCode(codeToUse);
      setAppliedCoupon(result.coupon);
      setCouponDiscount(result.discount);
      setCouponError('');
      showToast(result.message, 'success');
    } else {
      // Fall back to backend for coupons not in the local list
      try {
        const data = await apiService.validateCouponCode(codeToUse, cartTotal);
        if (data?.valid) {
          const c = data.coupon;
          setCouponCode(codeToUse);
          setAppliedCoupon({ code: c.code, description: c.description, type: c.type, discount: c.value, minOrder: null, maxDiscount: null });
          setCouponDiscount(c.discount_amount);
          setCouponError('');
          showToast(`Coupon applied! You save ₹${c.discount_amount}`, 'success');
        } else {
          setCouponError(data?.message || result.error);
          setAppliedCoupon(null);
          setCouponDiscount(0);
        }
      } catch (err) {
        setCouponError(err?.response?.data?.message || result.error);
        setAppliedCoupon(null);
        setCouponDiscount(0);
      }
    }
  };

  const autoApplyFirstOrderCoupon = async () => {
    if (appliedCoupon) return;
    if (orders.length > 0) return;
    try {
      const cartTotal = cartHelpers.getCartTotal(cart);
      const data = await apiService.getFirstOrderCoupon(cartTotal);
      if (!data?.coupon) return;
      const c = data.coupon;
      setFirstOrderCouponData(c);
      if (cartTotal >= (c.min_order_amount || 0) && c.discount_amount > 0) {
        setCouponCode(c.code);
        setAppliedCoupon({ code: c.code, description: c.description, type: c.type, discount: c.value, minOrder: c.min_order_amount, maxDiscount: c.max_discount, firstTimeOnly: true });
        setCouponDiscount(c.discount_amount);
        setCouponError('');
        showToast(`First order discount applied! You save ₹${c.discount_amount}`, 'success');
      }
    } catch (e) {
      // silent — auto-apply is best-effort
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponError('');
  };

  const getShippingCharge = () => {
    const cartTotal = cartHelpers.getCartTotal(cart);
    // Free shipping for orders above threshold (configurable from backend)
    return cartTotal >= appSettings.freeShippingThreshold ? 0 : appSettings.standardShippingFee;
  };

  const getTaxAmount = () => {
    const cartTotal = cartHelpers.getCartTotal(cart);
    // Tax rate (configurable from backend, default 5% GST)
    return (cartTotal * (appSettings.taxRate / 100)).toFixed(2);
  };

  const getFinalTotal = () => {
    const cartTotal = cartHelpers.getCartTotal(cart);
    const afterDiscount = couponHelpers.calculateFinalAmount(cartTotal, couponDiscount);
    const shipping = getShippingCharge();
    const tax = parseFloat(getTaxAmount());
    return (afterDiscount + shipping + tax).toFixed(2);
  };

  const getSuggestedCoupons = () => {
    const cartTotal = cartHelpers.getCartTotal(cart);
    const suggestions = couponHelpers.getSuggestedCoupons(cartTotal);
    if (firstOrderCouponData && orders.length === 0) {
      const alreadyIn = suggestions.some(c => c.code === firstOrderCouponData.code);
      if (!alreadyIn) {
        const foc = {
          code: firstOrderCouponData.code,
          discount: firstOrderCouponData.value,
          type: firstOrderCouponData.type,
          minOrder: firstOrderCouponData.min_order_amount || 0,
          maxDiscount: firstOrderCouponData.max_discount,
          description: firstOrderCouponData.description || 'First order discount',
          firstTimeOnly: true,
        };
        return [foc, ...suggestions].slice(0, 3);
      }
    }
    return suggestions;
  };



  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (authStep === 'otp' && timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(countdown);
    }
  }, [authStep, timer]);

  useEffect(() => {
    const isAvailable = window.PublicKeyCredential !== undefined;
    setBiometricAvailable(isAvailable);
  }, []);

  // Helper Functions

const addToCart = async (product, quantity = 1) => {
  try {
    // Ensure cart is an array
    const currentCart = Array.isArray(cart) ? cart : [];
    const updatedCart = await cartHelpers.addToCart(currentCart, product, quantity);
    setCart(updatedCart);
    console.log(`${product.name} added to cart!`);
  } catch (error) {
    console.error('Error adding to cart:', error);
    console.log('Failed to add to cart. Please try again.');
  }
};

// Toggle favorite for a product (adds/removes locally and via API when available)
const toggleFavorite = async (product) => {
  const productId = product?.id;
  if (productId === undefined || productId === null) {
    return;
  }

  const previousFavorites = Array.isArray(favorites) ? favorites : [];
  const existing = previousFavorites.find((item) => isSameProductId(item.id, productId));

  try {
    if (existing) {
      const newFavorites = previousFavorites.filter((item) => !isSameProductId(item.id, productId));
      syncFavoritesState(newFavorites);

      if (isAuthenticated && apiService && typeof apiService.removeFromFavorites === 'function') {
        const favoriteIdToDelete = existing.favorite_id || existing.favoriteId || existing.id;
        const removeResponse = await apiService.removeFromFavorites(favoriteIdToDelete);
        if (!removeResponse?.success) {
          throw new Error(removeResponse?.data?.message || 'Failed to remove favorite');
        }
      }

      showToast && showToast(`${product.name} removed from favorites`, 'info');
    } else {
      const newFavorites = normalizeFavorites([...(previousFavorites || []), product]);
      syncFavoritesState(newFavorites);

      if (isAuthenticated && apiService && typeof apiService.addToFavorites === 'function') {
        const addResponse = await apiService.addToFavorites(productId);
        if (!addResponse?.success) {
          throw new Error(addResponse?.data?.message || 'Failed to add favorite');
        }

        const favoriteRecord = addResponse?.data?.favorite || addResponse?.data?.data || null;
        const favoriteId = favoriteRecord?.id || favoriteRecord?.favorite_id || null;

        if (favoriteId) {
          const updatedFavorites = newFavorites.map((item) => (
            isSameProductId(item.id, productId)
              ? { ...item, favorite_id: toNumericIdIfPossible(favoriteId) }
              : item
          ));
          syncFavoritesState(updatedFavorites);
        } else {
          await loadFavorites();
        }
      }

      showToast && showToast(`${product.name} added to favorites`, 'success');
    }
  } catch (err) {
    console.error('Error toggling favorite:', err);
    syncFavoritesState(previousFavorites);

    if (isAuthenticated) {
      try {
        await loadFavorites();
      } catch (refreshError) {
        console.warn('Failed to refresh favorites after toggle error', refreshError);
      }
    }

    showToast && showToast('Failed to update favorites', 'error');
  }
};

  const addToCart_old = (product) => {
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      setCart(cart.map(item =>
        item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const updateQuantity_old = (id, delta) => {
    setCart(cart.map(item =>
      item.id === id ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item
    ).filter(item => item.quantity > 0));
  };

const updateQuantity = async (id, delta) => {
  console.log('updateQuantity called:', { id, delta });
  try {
    // Ensure cart is an array
    const currentCart = Array.isArray(cart) ? cart : [];
    const updatedCart = await cartHelpers.updateQuantity(currentCart, id, delta);
    setCart(updatedCart);
  } catch (error) {
    console.error('Error updating quantity:', error);
    console.log('Failed to update quantity. Please try again.');
  }
};

  const removeFromCart_old = (id) => {
    setCart(cart.filter(item => item.id !== id));
  };

const removeFromCart = async (id) => {
  try {
    // Ensure cart is an array
    const currentCart = Array.isArray(cart) ? cart : [];
    const updatedCart = await cartHelpers.removeFromCart(currentCart, id);
    setCart(updatedCart);
  } catch (error) {
    console.error('Error removing from cart:', error);
    console.log('Failed to remove item. Please try again.');
  }
};

  const getCartTotal_old = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getCartTotal = () => {
    if (!Array.isArray(cart)) return 0;
    return cartHelpers.getCartTotal(cart);
  };


  const getCartCount_old = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getCartCount = () => {
    if (!Array.isArray(cart)) return 0;
    return cartHelpers.getCartCount(cart);
};

  const clearCart = async () => {
    try {
      await cartHelpers.clearCart();
      setCart([]);
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };
  const deliverySlots = (appSettings.deliverySlots || []).filter(
    s => s.enabled !== false && s.enabled !== 0 && s.enabled !== '0' && s.enabled !== 'false'
  );

  // Sync selected delivery slot when API slots or default slot changes
  useEffect(() => {
    if (deliverySlots.length === 0) return;
    const preferred = appSettings.defaultDeliverySlot || deliverySlot;
    const exists = deliverySlots.some(s => s.id === preferred);
    if (!exists) {
      setDeliverySlot(deliverySlots[0].id);
    } else if (appSettings.defaultDeliverySlot && deliverySlot === 'morning' && appSettings.defaultDeliverySlot !== 'morning') {
      // Apply backend default only on first settings load (while still at the hardcoded initial value)
      setDeliverySlot(appSettings.defaultDeliverySlot);
    }
  }, [appSettings.deliverySlots, appSettings.defaultDeliverySlot]);

  // Re-fetch settings when checkout opens to pick up any admin changes to slots
  useEffect(() => {
    if (showCheckout) {
      loadSettings();
    }
  }, [showCheckout]);


  const processPayment_old = () => {
    console.log(`Payment processed via ${paymentMethod.toUpperCase()}!\nDelivery scheduled for ${deliverySlots.find(s => s.id === deliverySlot)?.label} slot.\nOrder confirmed!`);
    setCart([]);
    setShowCheckout(false);
    setActiveTab('orders');
  };

  const processPayment = async ({ walletAmountToUse = 0 } = {}) => {
    const orderTotal = getFinalTotal();

    // Wallet fully covers the order — place directly, skip payment modal
    if (walletAmountToUse >= orderTotal && orderTotal > 0) {
      try {
        setLoading(true);
        const cartItems = Array.isArray(cart) ? cart : [];
        const formattedItems = cartItems.map(item => {
          const effectivePrice = getFinalPrice(item);
          return {
            product_id: item.id || item.product_id,
            quantity: item.quantity || 1,
            price: effectivePrice || 0,
            original_price: item.price || 0,
            sale_price: item.sale_price || null,
            name: item.name || item.product_name || ''
          };
        });

        const orderData = {
          deliveryAddressesId: selectedAddressId,
          items: formattedItems,
          totalAmount: orderTotal,
          originalAmount: getCartTotal(),
          couponCode: appliedCoupon?.code || null,
          couponDiscount: couponDiscount || 0,
          shippingCharge: getShippingCharge(),
          taxAmount: parseFloat(getTaxAmount()),
          paymentMethod: 'wallet',
          walletAmountUsed: walletAmountToUse,
          deliverySlot: deliverySlot
        };

        const response = await orderHelpers.placeOrder(orderData);
        if (response.success && response.order?.id) {
          const defaultAddress = deliveryAddresses.find(addr => addr.isDefault) || deliveryAddresses[0];
          setOrderDetails({
            orderId: response.order.id,
            orderNumber: response.order.order_number || response.order.id,
            items: cartItems.map(item => ({ ...item, icon: item.icon || '🛒' })),
            totalAmount: orderTotal,
            originalAmount: getCartTotal(),
            couponCode: appliedCoupon?.code || null,
            couponDiscount: couponDiscount || 0,
            shippingCharge: getShippingCharge(),
            taxAmount: parseFloat(getTaxAmount()),
            deliverySlot: deliverySlot,
            paymentMethod: 'wallet',
            walletAmountUsed: walletAmountToUse,
            deliveryAddress: defaultAddress,
            estimatedDelivery: 'Tomorrow, ' + new Date(Date.now() + 86400000).toLocaleDateString('en-IN', {
              weekday: 'short', month: 'short', day: 'numeric'
            })
          });
          await clearCart();
          setCart([]);
          handleRemoveCoupon();
          setShowCheckout(false);
          setShowOrderConfirmation(true);
          setActiveTab('home');
          loadOrders(1);
          fetchWallet();
          showToast('Order placed using wallet balance!', 'success');
        } else {
          showToast('Failed to place order. Please try again.', 'error');
        }
      } catch (err) {
        console.error('Error placing wallet-paid order:', err);
        showToast('Failed to place order. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
      return;
    }

    // COD / UPI Scan on Delivery – open payment confirmation modal directly
    if (paymentMethod === 'cod' || paymentMethod === 'upi_cod') {
      setPendingWalletAmount(walletAmountToUse);
      setShowCheckout(false);
      setShowPayment(true);
      return;
    }

    // Online payments (PayU) — place the order first so we have an order ID
    try {
      setLoading(true);
      const cartItems = Array.isArray(cart) ? cart : [];
      const formattedItems = cartItems.map(item => {
        const effectivePrice = getFinalPrice(item);
        return {
          product_id: item.id || item.product_id,
          quantity: item.quantity || 1,
          price: effectivePrice || 0,
          original_price: item.price || 0,
          sale_price: item.sale_price || null,
          name: item.name || item.product_name || ''
        };
      });

      const orderData = {
        deliveryAddressesId: selectedAddressId,
        items: formattedItems,
        totalAmount: orderTotal,
        originalAmount: getCartTotal(),
        couponCode: appliedCoupon?.code || null,
        couponDiscount: couponDiscount || 0,
        shippingCharge: getShippingCharge(),
        taxAmount: parseFloat(getTaxAmount()),
        paymentMethod: paymentMethod,
        walletAmountUsed: walletAmountToUse,
        deliverySlot: deliverySlot
      };

      const response = await orderHelpers.placeOrder(orderData);
      const createdOrderId = response?.order?.id || response?.order_id || response?.id || null;

      if (response.success && createdOrderId) {
        setPendingOrderId(createdOrderId);
        setShowCheckout(false);
        setShowPayment(true);
      } else {
        showToast('Failed to create order. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error pre-placing order for PayU:', err);
      showToast('Failed to create order. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentComplete = async (paymentResult = null) => {
    const callbackOrderId = paymentResult?.orderId || null;
    const callbackTxnId = paymentResult?.txnid || null;
    const isCodFlow = paymentMethod === 'cod' || paymentMethod === 'upi_cod'
      || paymentResult?.paymentMethod === 'cod' || paymentResult?.paymentMethod === 'upi_cod';

    if (!isCodFlow) {
      let resolvedOrderId = null;
      const numericCallbackOrderId = Number(callbackOrderId);
      const numericPendingOrderId = Number(pendingOrderId);

      if (Number.isFinite(numericCallbackOrderId) && numericCallbackOrderId > 0) {
        resolvedOrderId = numericCallbackOrderId;
      } else if (Number.isFinite(numericPendingOrderId) && numericPendingOrderId > 0) {
        resolvedOrderId = numericPendingOrderId;
      }

      try {
        if (!resolvedOrderId && callbackTxnId) {
          const verifyResp = await apiService.verifyPayuTransaction(callbackTxnId);
          const verifiedOrderId =
            verifyResp?.data?.order?.id ||
            verifyResp?.order?.id ||
            verifyResp?.data?.payment?.order_id ||
            verifyResp?.payment?.order_id ||
            null;

          const numericVerifiedOrderId = Number(verifiedOrderId);
          if (Number.isFinite(numericVerifiedOrderId) && numericVerifiedOrderId > 0) {
            resolvedOrderId = numericVerifiedOrderId;
          }
        }

        if (!resolvedOrderId) {
          throw new Error('Order ID missing after PayU success');
        }

        const orderResponse = await apiService.getOrderById(resolvedOrderId);
        const order = orderResponse?.data?.order || orderResponse?.data || null;

        if (!order) {
          throw new Error('Order details not available after payment');
        }

        const deliveryAddr = {
          address: order.shipping_address || '',
          city: order.shipping_city || '',
          state: order.shipping_state || '',
          pincode: order.shipping_pincode || '',
        };

        const rawItems = order?.items || order?.order_items || [];
        const normalizedItems = rawItems.map(item => ({
          ...item,
          name: item.name || item.product_name || item.product?.name || '',
          price: parseFloat(item.price || 0),
          quantity: item.quantity || 1,
          icon: item.icon || 'ðŸ¥›',
        }));

        const confirmationDetails = {
          orderId: order?.id || resolvedOrderId,
          orderNumber: order?.order_number || resolvedOrderId,
          paymentTxnId: callbackTxnId || order?.transaction_id || null,
          items: normalizedItems,
          totalAmount: order?.total || 0,
          originalAmount: order?.subtotal || order?.total || 0,
          couponCode: order?.coupon_code || appliedCoupon?.code || null,
          couponDiscount: order?.coupon_discount || couponDiscount || 0,
          shippingCharge: order?.shipping_fee || 0,
          taxAmount: order?.tax || 0,
          deliverySlot: order?.delivery_slot || deliverySlot,
          paymentMethod: order?.payment_method || paymentMethod,
          deliveryAddress: order?.delivery_address || deliveryAddr,
          estimatedDelivery: 'Tomorrow, ' + new Date(Date.now() + 86400000).toLocaleDateString('en-IN', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
          })
        };

        setOrderDetails(confirmationDetails);
        await clearCart();
        setCart([]);
        handleRemoveCoupon();
        setShowPayment(false);
        setShowOrderConfirmation(true);
        setShowCheckout(false);
        setActiveTab('home');
        loadOrders(1);
        showToast('Payment successful! Your order has been confirmed.', 'success');
      } catch (error) {
        console.error('Error finalizing PayU payment:', error);
        setShowPayment(false);
        setShowCheckout(false);
        setActiveTab('orders');
        loadOrders(1);
        showToast('Payment successful. Your order is being verified.', 'success');
      } finally {
        setPendingOrderId(null);
      }

      return;
    }

    try {
      console.log(`Payment processed via ${paymentMethod.toUpperCase()}!\nDelivery scheduled for ${deliverySlots.find(s => s.id === deliverySlot)?.label} slot.\nOrder confirmed!`);

      const cartItems = Array.isArray(cart) ? cart : [];

      const formattedItems = cartItems.map(item => {
        const effectivePrice = getFinalPrice(item);
        return {
          product_id: item.id || item.product_id,
          quantity: item.quantity || 1,
          price: effectivePrice || 0,
          original_price: item.price || 0,
          sale_price: item.sale_price || null,
          name: item.name || item.product_name || ''
        };
      });

      const orderData = {
        deliveryAddressesId: selectedAddressId,
        items: formattedItems,
        totalAmount: getFinalTotal(),
        originalAmount: getCartTotal(),
        couponCode: appliedCoupon?.code || null,
        couponDiscount: couponDiscount || 0,
        shippingCharge: getShippingCharge(),
        taxAmount: parseFloat(getTaxAmount()),
        paymentMethod: paymentMethod,
        walletAmountUsed: pendingWalletAmount,
        deliverySlot: deliverySlot
      };

      console.log('Order Data being sent:', orderData);

      const response = await orderHelpers.placeOrder(orderData);
      console.log('Order Response:', response);
      if (response.success && response.order.id) {
        console.log('Order ID:', response.order.id);

        const defaultAddress = deliveryAddresses.find(addr => addr.isDefault) || deliveryAddresses[0];

        const confirmationDetails = {
          orderId: response.order.id,
          orderNumber: response.order.order_number || response.order.id,
          items: cartItems.map(item => ({
            ...item,
            icon: item.icon || 'ðŸ¥›'
          })),
          totalAmount: getFinalTotal(),
          originalAmount: getCartTotal(),
          couponCode: appliedCoupon?.code || null,
          couponDiscount: couponDiscount || 0,
          shippingCharge: getShippingCharge(),
          taxAmount: parseFloat(getTaxAmount()),
          deliverySlot: deliverySlot,
          paymentMethod: paymentMethod,
          deliveryAddress: defaultAddress,
          estimatedDelivery: 'Tomorrow, ' + new Date(Date.now() + 86400000).toLocaleDateString('en-IN', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
          })
        };

        setOrderDetails(confirmationDetails);

        await clearCart();
        setCart([]);
        handleRemoveCoupon();
        setPendingWalletAmount(0);
        fetchWallet();

        setShowPayment(false);
        setShowOrderConfirmation(true);
        setShowCheckout(false);
        setActiveTab('home');

      } else {
        console.log('Order placed but no order ID returned.');
        setShowPayment(false);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      console.log('Payment processed but failed to clear cart. Please refresh the page.');
      setShowPayment(false);
    }
  };

  const subscribeProduct = async (product, plan, paymentMethod = 'wallet') => {
    const localSubscription = {
      addressId: deliveryAddresses.find(addr => addr.isDefault)?.id || deliveryAddresses[0]?.id,
      productId: product.id || product.product_id || null,
      productName: product.name || product.product_name || '',
      planId: plan.id,
      planName: plan.name,
      startDate: new Date().toISOString(),
      deliverySlot: 'morning',
      status: 'active',
      paymentMethod: paymentMethod,
    };

    try {
      setLoading(true);
      // Try persisting to backend
      const resp = await apiService.createSubscription(localSubscription);
      if (resp && resp.success) {
        const created = resp.data?.subscription || { ...localSubscription, id: resp.data?.id };
        setSubscription(created);
        localStorage.setItem('subscription', JSON.stringify(created));
        showToast && showToast('Subscription activated successfully', 'success');
        console.log(`Subscription activated on server for ${product.name}`);
      } else {
        // Fallback to localStorage when API fails
        setSubscription(localSubscription);
        localStorage.setItem('subscription', JSON.stringify(localSubscription));
        showToast && showToast('Subscription saved locally (offline)', 'warning');
        console.log('Subscription saved locally (API failed)');
      }
    } catch (err) {
      console.error('Error creating subscription:', err);
      // Fallback to local
      setSubscription(localSubscription);
      localStorage.setItem('subscription', JSON.stringify(localSubscription));
      showToast && showToast('Error activating subscription; saved locally', 'error');
    } finally {
      setLoading(false);
      setShowSubscription(null);
      setEditingSubscription(null);
    }
  };

  const updateSubscriptionPlan = async (subscriptionId, planData) => {
    try {
      setLoading(true);
      const resp = await apiService.updateSubscriptionPlan(subscriptionId, planData);
      if (resp && resp.success) {
        const updated = resp.data?.subscription || { ...subscription, planId: planData.planId };
        setSubscription(updated);
        localStorage.setItem('subscription', JSON.stringify(updated));
        showToast && showToast('Subscription plan updated', 'success');
      } else {
        showToast && showToast('Failed to update plan on server', 'error');
      }
    } catch (err) {
      console.error('Error updating subscription plan:', err);
      showToast && showToast('Error updating plan', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleModifySubscription = (sub) => {
    setEditingSubscription(sub);
    // open modal with product details if available
    setShowSubscription(sub.product || { id: sub.productId, name: sub.productName, price: sub.price || 0, description: sub.product?.description || '' });
  };

  const sendOTPForLogin = async (method) => {   
  if (mobile.length === 10) {
    const loginPassword = password || 'defaultPassword'; // Use default if not set
    const result = await handleSendOTPForLogin(mobile, loginPassword, method);
    
    if (result.success) {
      setError(''); // Clear any previous errors
      setMobileNotFound(false);
      setOtpMethod(result.deliveryMethod || method);
      setAuthStep('otp');
      setTimer(120);
      setCanResend(false);
      const methodText = method === 'sms' ? 'SMS' : 'WhatsApp';
      console.log(`OTP sent via ${methodText} to ${mobile}\n\nDemo OTP: 123456`);
    }else {
      setError(result.error);
      console.log('Login failed: ' + result.error);
    }
    // Error already shown in handleSendOTPForLogin
  } else {
    setError('Please enter a valid 10-digit phone number');
    console.log('Please enter a valid 10-digit phone number');
  }
};

  const sendOTP = (method) => {
    if (mobile.length === 10) {
      setError(''); // Clear any previous errors
      handleSendOTP(mobile, method);
      setOtpMethod(method);
      setAuthStep('otp');
      setTimer(120);
      setCanResend(false);
      const methodText = method === 'sms' ? 'SMS' : 'WhatsApp';
      console.log(`OTP sent via ${methodText} to ${mobile}\n\nDemo OTP: 123456`);
    } else {
      setError('Please enter a valid 10-digit phone number');
      console.log('Please enter a valid 10-digit phone number');
    }
  };

  const resendOTP = () => {
    setError(''); // Clear any previous errors
    setTimer(120);
    setCanResend(false);
    setOtp(['', '', '', '', '', '']);
    console.log(`OTP resent via ${otpMethod === 'sms' ? 'SMS' : 'WhatsApp'} to ${mobile}\n\nDemo OTP: 123456`);
  };

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        document.getElementById(`otp-${index + 1}`)?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleFullOtpChange = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setOtp([...digits.split(''), ...Array(6 - digits.length).fill('')]);
  };

  const verifyOTP = async () => {
  const enteredOtp = otp.join('');
 
  if (enteredOtp.length === 6) {
    try {
      setLoading(true);
      setError(null); // Clear previous errors
      
      // Demo mode: Accept 123456 as valid OTP
      /* Uncomment below to enable demo mode bypass
      if (enteredOtp === '123456') {
        console.log('âœ… OTP verified successfully (Demo Mode)!');
        
        // Mock user data for demo - simulate new user without profile
        const mockUser = {
          name: '',  // Empty name indicates new user
          email: '',
          mobile: mobile,
          whatsapp_enabled: false  // New user needs to set up WhatsApp
        };
        
        // Don't set profile data yet - let user complete it
        setUserProfile({
          name: '',
          email: '',
          address: '',
          mobile: mockUser.mobile,
          city: '',
          pincode: '',
          profilePic: null
        });
        
        // New user - ask for WhatsApp connection first
        console.log('New user detected. Proceeding to WhatsApp setup...');
        addSecurityLog('OTP Verified', 'Mobile OTP verified successfully (Demo Mode)', 'success');
        setWhatsAppNumber(mobile);
        setAuthStep('whatsapp');
        setLoading(false);
        return;
      }
      */
      // Try backend API call
      const response = await apiService.verifyMobileOtp(mobile, enteredOtp);
      if (response.success) {
        console.log('OTP verified successfully!');
        let authUser = null;

        try {
          const persisted = persistAuthPayload(response.data, 'mobile OTP login');
          authUser = persisted.user;
        } catch (persistError) {
          console.error('OTP auth persistence failed:', persistError);
          setError('Login session could not be created. Please try again.');
          return;
        }

        console.log('User data: ' + JSON.stringify(authUser || {}));
        console.log('Token persisted for OTP login.');
        
        // Profile is "complete" once the user has finished onboarding (name +
        // default address, tracked by profile_completed). WhatsApp is optional and
        // must never force the connection screen again.
        const profileComplete = Boolean(
          authUser && (authUser.profile_completed || (authUser.name && authUser.has_default_address))
        );

        if (profileComplete) {
          // Existing onboarded user - log them in directly
          setAuthenticatedSession(authUser);
          try { await loadFavorites(); } catch(e) { console.warn('loadFavorites failed after verifyOTP', e); }
          console.log(`Welcome back, ${authUser.name}!`);
          addSecurityLog('Login Successful', 'Logged in via Mobile OTP', 'success');
          setAuthStep('completed');
        } else {
          // New / incomplete user - run onboarding (WhatsApp then profile)
          setWhatsAppNumber(mobile);
          setAuthStep('whatsapp');
          setIsAuthenticated(false);
          setShowAuthModal(true);
          if (authUser) {
            hydrateUserProfile(authUser);
          }
        }
      } else {
        // Handle specific error responses
        let errorMessage = 'Invalid OTP. Please try again.';
        
        if (response.status === 401) {
          errorMessage = 'Invalid OTP. Demo mode: Use 123456';
        } else if (response.status === 404) {
          errorMessage = 'Mobile number not found. Demo mode: Use 123456';
        } else if (response.status === 429) {
          errorMessage = 'Too many attempts. Please try again later.';
        } else if (response.status === 0) {
          // Network error - likely no backend
          errorMessage = 'Backend not available. Demo mode: Use OTP 123456';
        } else if (response.data?.message) {
          errorMessage = response.data.message;
          
          // Filter out generic validation messages
          if (errorMessage.toLowerCase().includes('validation failed') ||
              errorMessage.toLowerCase().includes('validation error')) {
            errorMessage = 'Invalid OTP. Please try again.';
          }
        }
        
        console.log('OTP verification failed: ' + errorMessage);
        setError(errorMessage);
        setOtp(['', '', '', '', '', '']);
        // Refocus the first input field after error
        setTimeout(() => {
          document.getElementById('otp-0')?.focus();
        }, 100);
      }
    } catch (err) {
      console.log('Error verifying OTP: ' + err.message);
      setError('Backend unavailable. Demo mode: Use OTP 123456');
      setOtp(['', '', '', '', '', '']);
      // Refocus the first input field after error
      setTimeout(() => {
        document.getElementById('otp-0')?.focus();
      }, 100);
    } finally {
      setLoading(false);
    }
  } else {
    console.log('Please enter a valid 6-digit OTP');
    setError('Please enter a valid 6-digit OTP');
    setOtp(['', '', '', '', '', '']);
    // Refocus the first input field
    setTimeout(() => {
      document.getElementById('otp-0')?.focus();
    }, 100);
  }
};

  const verifyOTP_old = async () => {
        const enteredOtp = otp.join('');
        
        if (enteredOtp.length === 6) {
          try {
            setLoading(true);
            const response = await apiService.verifyMobileOtp(mobile, enteredOtp);
            
            if (response.success) {
              // Don't set isAuthenticated yet - let user complete profile first
              console.log('OTP verified successfully!');
              console.log('User data: '+JSON.stringify(response.data.user));
              console.log('Token: '+response.data.token);
              // Store token and user data
              apiService.setToken(response.data.token);
              apiService.setUser(response.data.user);
              console.log('Checking user profile...'+response.data.user.whatsapp_enabled);
              // Check if user has existing profile
              if (response.data.user && response.data.user.name && response.data.user.whatsapp_enabled) {
                // Existing user - log them in directly
                setUserProfile({
                  name: response.data.user.name,
                  email: response.data.user.email || '',
                  address: response.data.user.address || '',
                  mobile: response.data.user.mobile || '',
                  city: response.data.user.city || '',
                  pincode: response.data.user.pincode || '',
                  profilePic: response.data.user.profilePic || null
                });
                setIsAuthenticated(true);
                setShowAuthModal(false);
                try { await loadFavorites(); } catch(e) { console.warn('loadFavorites failed in verifyOTP_old', e); }
                console.log(`Welcome back, ${response.data.user.name}!`);
                addSecurityLog('Login Successful', 'Logged in via Mobile OTP', 'success');
                setAuthStep('completed');
               // setActiveTab('home');
              } else {
                // New user - ask for WhatsApp connection first
                setWhatsAppNumber(mobile);
                setAuthStep('whatsapp');
              }
            } else {
              console.log('OTP verification failed: ' + (response.data?.message || response.message));
            }
          } catch (err) {
            console.log('Error verifying OTP: ' + err.message);
            setError(err.message);
            setOtp(['', '', '', '', '', '']);
          } finally {
            setLoading(false);
          }
        } else {
          console.log('Please enter a valid 6-digit OTP');
        }
  };

  const connectWhatsApp = async () => {
      try {
        setLoading(true);
        
        // Demo mode: Skip API call
        const isDemo = !apiService.getToken();
        
        if (isDemo) {
          console.log('âœ… WhatsApp connected successfully (Demo Mode)!');
          setUserProfile({ 
            ...userProfile, 
            whatsappConnected: true 
          });
          addSecurityLog('WhatsApp Connected', `WhatsApp notifications enabled for ${mobile} (Demo Mode)`, 'success');
          setAuthStep('profile');
          setLoading(false);
          return;
        }
        
        // Call API to save WhatsApp preference
        const response = await apiService.connectWhatsApp(whatsAppNumber);
        
        if (response.success) {
          // Update user profile with WhatsApp status
          setUserProfile({ 
            ...userProfile, 
            whatsappConnected: true 
          });
          setUserProfile({
                  name: response.data.user.name,
                  email: response.data.user.email || '',
                  address: response.data.user.address || '',
                  mobile: response.data.user.mobile || '',
                  city: response.data.user.city || '',
                  pincode: response.data.user.pincode || '',
                  profilePic: response.data.user.profilePic || null,  
                  whatsappConnected: true 
          });
          // Log the security event
          addSecurityLog('WhatsApp Connected', `WhatsApp notifications enabled for ${mobile}`, 'success');
          
          // Move to profile completion step
          setAuthStep('profile');
          
          console.log('WhatsApp connected successfully!\n\nYou will receive order updates on WhatsApp.');
        } else {
          console.log('Failed to connect WhatsApp: ' + (response.data?.message || 'Unknown error'));
          setError(response.data?.message || 'Unknown error');
          setOtp(['', '', '', '', '', '']);
          // Still proceed to profile on error
          setAuthStep('profile');
        }
      } catch (err) {
        console.error('WhatsApp connection error:', err);
        console.log('Error connecting WhatsApp. You can enable it later from Settings.');
        setError('Error connecting WhatsApp. You can enable it later from Settings.');
        setOtp(['', '', '', '', '', '']);
        // Still proceed to profile on error
        setAuthStep('profile');
      } finally {
        setLoading(false);
      }
    };

  const skipWhatsApp = () => {
    // Log that user skipped WhatsApp
    addSecurityLog('WhatsApp Skipped', 'User chose to skip WhatsApp connection', 'info');
    setAuthStep('profile');
  };

  const updateWhatsAppPreference = async (enabled) => {
    try {
      setLoading(true);
      
      const response = await apiService.updateWhatsAppPreference(enabled ? whatsAppNumber : null, enabled);
      
      if (response.success) {
        setUserProfile({ 
          ...userProfile, 
          whatsappConnected: enabled 
        });
        
        addSecurityLog(
          enabled ? 'WhatsApp Enabled' : 'WhatsApp Disabled', 
          `User ${enabled ? 'enabled' : 'disabled'} WhatsApp notifications`, 
          'info'
        );
        
        console.log(`âœ… WhatsApp notifications ${enabled ? 'enabled' : 'disabled'} successfully!`);
      } else {
        console.log('Failed to update WhatsApp preference: ' + (response.data?.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error updating WhatsApp preference:', err);
      console.log('Error updating WhatsApp preference. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const checkPasswordStrength = (pwd) => {
    setPassword(pwd);
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) score++;
    
    const strengths = [
      { score: 0, label: '', color: '' },
      { score: 1, label: 'Very Weak', color: 'bg-red-500' },
      { score: 2, label: 'Weak', color: 'bg-orange-500' },
      { score: 3, label: 'Fair', color: 'bg-yellow-500' },
      { score: 4, label: 'Good', color: 'bg-blue-500' },
      { score: 5, label: 'Strong', color: 'bg-green-500' }
    ];
    
    setPasswordStrength(strengths[score]);
  };

  const handleEmailLogin = async (email, pwd) => {
    if (!email || !pwd) {
      console.log('Please enter both email and password');
      setError('Please enter both email and password');
      setOtp(['', '', '', '', '', '']);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await apiService.login({
        email,
        password: pwd,
        remember: rememberMe,
      });

      if (response?.success) {
        let authUser = null;

        try {
          const persisted = persistAuthPayload(response.data, 'email login');
          authUser = persisted.user;
        } catch (persistError) {
          console.error('Email auth persistence failed:', persistError);
          setError('Login session could not be created. Please try again.');
          return;
        }

        if (authUser) {
          setAuthenticatedSession(authUser);
        } else {
          setAuthenticatedSession();
        }

        setAuthStep('completed');
        try { await loadFavorites(); } catch (e) { console.warn('loadFavorites failed after email login', e); }
        console.log(`Welcome back, ${authUser?.name || authUser?.email || email}!`);
      } else {
        const message = response?.data?.message || 'Invalid email or password';
        setError(message);
        console.log('Email login failed:', message);
      }
    } catch (err) {
      const message = err?.message || 'Email login failed. Please try again.';
      console.error('Email login error:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    console.log(`ðŸ” Logging in with ${provider}...\n\nIn production, this would redirect to ${provider} OAuth.`);
    setTimeout(() => {
      setAuthStep('profile');
      setLoginMethod('social');
      if (provider === 'Google') {
        setUserProfile({ ...userProfile, email: 'user@gmail.com' });
      } else {
        setUserProfile({ ...userProfile, email: 'user@facebook.com' });
      }
    }, 1000);
  };

  const handleProfilePicUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUserProfile({ ...userProfile, profilePic: event.target.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const searchAddress = (query) => {
    setUserProfile({ ...userProfile, address: query });
    if (query.length < 3) {
      setShowAddressSuggestions(false);
      return;
    }
   
    setAddressSuggestions(mockSuggestions(query));
    setShowAddressSuggestions(true);
  };

  const selectAddress = (suggestion) => {
    setUserProfile({
      ...userProfile,
      address: suggestion.address,
      city: suggestion.city,
      pincode: suggestion.pincode
    });
    setShowAddressSuggestions(false);
  };

  const handleProfileSubmit = async () => {
    if (userProfile.name && userProfile.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userProfile.email)) {
        console.log('Please enter a valid email address');
        setError('Please enter a valid email address');
        return;
      }

      const updatedUser = {
        ...userProfile
      };

      try {
        setLoading(true);
        const resp = await apiService.updateUserProfile(updatedUser);

        if (resp && resp.success) {
          // Persist returned user if available
          if (resp.data && resp.data.user) {
            apiService.setUser(resp.data.user);
            setUserProfile({
              name: resp.data.user.name || userProfile.name,
              email: resp.data.user.email || userProfile.email,
              address: resp.data.user.address || userProfile.address,
              mobile: resp.data.user.mobile || userProfile.mobile,
              city: resp.data.user.city || userProfile.city,
              pincode: resp.data.user.pincode || userProfile.pincode,
              state: resp.data.user.state || userProfile.state,
              country: resp.data.user.country || userProfile.country,
              profilePic: resp.data.user.profilePic || userProfile.profilePic
            });
          }

          // Create a delivery address from the personal address ONLY if the user
          // has none yet — never duplicate an existing delivery address.
          if (userProfile.address) {
            let hasExistingAddress = false;
            try {
              const existing = await apiService.fetchDeliveryAddresses();
              hasExistingAddress = Boolean(
                existing?.success &&
                Array.isArray(existing.data?.addresses) &&
                existing.data.addresses.some(a => a && a.address)
              );
            } catch (e) {
              hasExistingAddress = Array.isArray(deliveryAddresses) && deliveryAddresses.some(a => a && a.address);
            }

            if (hasExistingAddress) {
              console.log("User already has a delivery address — skipping creation from profile.");
              try { await loadDeliveryAddresses(); } catch (_) {}
            } else {
              const addressPayload = {
                label: 'Home',
                recipient_name: userProfile.name,
                address: userProfile.address,
                city: userProfile.city || '',
                state: userProfile.state || '',
                country: userProfile.country || 'India',
                landmark: userProfile.landmark || '',
                pincode: userProfile.pincode || '',
                isDefault: true,
                phone: userProfile.mobile || mobile,
                latitude: userProfile.latitude || 28.4595,
                longitude: userProfile.longitude || 77.0266
              };

              try {
                const addResp = await apiService.addDeliveryAddress(addressPayload);
                if (addResp && addResp.success) {
                  const newAddr = addResp.data?.address || { ...addressPayload, id: addResp.data?.id || Date.now() };
                  setDeliveryAddresses(prev => [
                    // mark previous addresses non-default
                    ...prev.map(a => ({ ...a, isDefault: false })),
                    { ...newAddr, isDefault: true }
                  ]);
                  console.log('âœ… Delivery address created from profile');
                  showToast && showToast('Delivery address created from profile', 'success');
                  // Refresh addresses from server to ensure latest state
                  try {
                    await loadDeliveryAddresses();
                  } catch (e) {
                    console.warn('Could not reload delivery addresses:', e);
                  }
                } else {
                  // Fallback: still set local default address
                  const localAddr = { ...addressPayload, id: Date.now() };
                  setDeliveryAddresses(prev => [
                    ...prev.map(a => ({ ...a, isDefault: false })),
                    localAddr
                  ]);
                  console.log('âš ï¸ Delivery address API failed, used local address');
                  showToast && showToast('Failed to create delivery address on server; using local address', 'warning');
                }
              } catch (err) {
                console.error('Error creating delivery address:', err);
                showToast && showToast('Error creating delivery address', 'error');
              }
            }
          }

          setIsAuthenticated(true);
          setShowAuthModal(false);
          setActiveTab('home');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          console.log(`âœ… Welcome ${userProfile.name}! Profile saved.`);
          console.log(`âœ… Welcome ${userProfile.name}!\n\nYour profile has been created successfully.`);
          addSecurityLog('Profile Created', `User profile created for ${userProfile.name}`, 'success');
          setAuthStep('completed');
          try { await loadFavorites(); } catch(e) { console.warn('loadFavorites failed after profile submit', e); }
        } else {
          console.log('Failed to update profile:', resp?.data?.message || resp?.message || 'Unknown error');
          setError(resp?.data?.message || 'Failed to update profile');
        }
      } catch (err) {
        console.error('Error updating profile:', err);
        setError('Error updating profile. Please try again later.');
      } finally {
        setLoading(false);
      }
    } else {
      console.log('Please fill in all required fields (Name and Email)');
      setError('Please fill in all required fields (Name and Email)');
      setOtp(['', '', '', '', '', '']);
    }
  };

  const enableBiometric = () => {
    setBiometricEnabled(true);
    // Persist a demo biometric token so biometric login can work offline/demo
    try {
      const token = apiService.getToken();
      if (token) {
        localStorage.setItem('biometric_token', token);
      }
    } catch (e) {
      console.warn('Could not persist biometric token', e);
    }
    console.log('âœ… Biometric login enabled!\n\nYou can now use fingerprint or face recognition to sign in quickly.');
  };
  const handleBiometricLogin = async () => {
    // Prefer real WebAuthn flow when available and backend-supported
    if (window.PublicKeyCredential && navigator.credentials && typeof navigator.credentials.get === 'function') {
      try {
        // In production, you'd request an assertion challenge from server here.
        // For demo/offline mode we fall back to stored token below.
        // Attempt a quick silent get (may prompt the authenticator)
        const assertion = await navigator.credentials.get({ publicKey: { challenge: new Uint8Array([0]) } });
        // If assertion returned, try to restore session using stored biometric token
        const saved = localStorage.getItem('biometric_token');
        if (saved) {
          apiService.setToken(saved);
          try {
            const resp = await apiService.getAuthenticatedUser();
            if (resp && resp.success && resp.data && resp.data.user) {
              apiService.setUser(resp.data.user);
              setUserProfile({
                name: resp.data.user.name || '',
                email: resp.data.user.email || '',
                mobile: resp.data.user.mobile || '',
                address: resp.data.user.address || '',
                city: resp.data.user.city || '',
                pincode: resp.data.user.pincode || '',
                profilePic: resp.data.user.profilePic || null
              });
              setIsAuthenticated(true);
              setShowAuthModal(false);
              try { await loadFavorites(); } catch (e) { console.warn('loadFavorites failed after biometric login', e); }
              addSecurityLog('Login Successful', 'Logged in via Biometric Authentication', 'success');
              console.log('âœ… Biometric login successful!\n\nWelcome back!');
              return;
            }
          } catch (err) {
            console.warn('Failed to validate biometric token with server:', err);
          }
        }
      } catch (err) {
        console.warn('WebAuthn assertion failed or was cancelled:', err);
        // continue to fallback
      }
    }

    // Fallback demo/offline biometric: restore token stored at enable time
    try {
      const saved = localStorage.getItem('biometric_token');
      if (saved) {
        apiService.setToken(saved);
        const storedUser = apiService.getUser();
        if (storedUser) {
          setUserProfile(storedUser);
        } else {
          try {
            const resp = await apiService.getAuthenticatedUser();
            if (resp && resp.success && resp.data && resp.data.user) {
              setUserProfile(resp.data.user);
            }
          } catch (e) {
            console.warn('Could not fetch user after restoring token', e);
          }
        }
        setIsAuthenticated(true);
        setShowAuthModal(false);
        try { await loadFavorites(); } catch (e) { console.warn('loadFavorites failed after biometric login fallback', e); }
        addSecurityLog('Login Successful', 'Logged in via Biometric (fallback)', 'success');
        console.log('âœ… Biometric login (fallback) successful!');
        return;
      }
    } catch (err) {
      console.error('Biometric fallback failed:', err);
    }

    // Final fallback: not supported or no saved credential
    setError('Biometric login not available. Please enable it in Settings or use password/OTP.');
    console.log('Biometric login not available');
  };
  const addSecurityLog = (action, detail, type) => {
    const newLog = {
      id: securityLogs.length + 1,
      action,
      detail,
      time: 'Just now',
      type
    };
    setSecurityLogs([newLog, ...securityLogs]);
  };

const handleLogout = () => {
  setShowLogoutConfirm(true);
};

const confirmLogout = async () => {
  try {
    await apiService.logout();
  } catch (error) {
    console.error('Logout failed, clearing local session anyway:', error);
    apiService.removeToken({ clearAppState: true, clearAllStorage: true });
  }

  // Reset authentication state
  setIsAuthenticated(false);
  setShowAuthModal(true);
  setShowLogoutConfirm(false);
  
  // Reset auth flow
  setAuthStep('phone');
  setMobile('');
  setOtp(['', '', '', '', '', '']);
  setPassword('');
  
  // Reset user data
  setUserProfile({
    name: '',
    email: '',
    address: '',
    mobile: '',
    city: '',
    pincode: '',
    profilePic: null
  });
  
  // Reset cart and subscription
  setCart([]);
  setSubscription(null);
  
  // Reset UI state
  setActiveTab('home');
  setNavHistory([]);
  setSearchQuery('');
  
  // Add security log
  addSecurityLog('Logout', 'User logged out manually', 'info');
  
  // Show success message
  console.log('âœ… Logged out successfully!');

  // Force clean app bootstrap after logout.
  window.location.replace('/');
};

const logoutAllDevices = () => {
    if (window.confirm('Are you sure you want to logout from all devices?')) {
      setLoginHistory(loginHistory.map(device => ({ ...device, current: device.current })));
      addSecurityLog('Logout All Devices', 'User logged out from all devices', 'info');
      console.log('âœ… Logged out from all devices successfully!');
    }
  };

  const logoutDevice = (deviceId) => {
    setLoginHistory(loginHistory.filter(device => device.id !== deviceId));
    addSecurityLog('Device Logout', `Logged out from device ID: ${deviceId}`, 'info');
    console.log('âœ… Device logged out successfully!');
  };

  // Add before return statement (around line 740)
    const handleAddAddress = async (newAddress) => {
      try {
        setLoading(true);
        
        if (editingAddress) {
          // Edit mode - update existing address
          const response = await apiService.updateDeliveryAddress(editingAddress.id, newAddress);
          
          if (response.success) {
            showToast('Address updated successfully!', 'success');
            await loadDeliveryAddresses();
          } else {
            showToast('Failed to update address: ' + (response.data?.message || 'Unknown error'), 'error');
          }
        } else {
          // Add mode - create new address
          const response = await apiService.addDeliveryAddress(newAddress);

          if (response.success) {
            showToast('Address added successfully!', 'success');
            await loadDeliveryAddresses();
          } else {
            showToast('Failed to add address: ' + (response.data?.message || 'Unknown error'), 'error');
          }
        }
        
        setEditingAddress(null); // Reset editing state
        setShowAddAddress(false); // Close modal
      } catch (err) {
        console.error('Error saving address:', err);
        showToast('Error saving address. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    };     

    const handleUpdateAddress = async (id, updatedAddress) => {
      const response = await apiService.updateDeliveryAddress(id, updatedAddress);
      if (!response.success) {
        console.log('âŒ Failed to update address: ' + (response.data?.message || 'Unknown error'));
        return;
      }
      setDeliveryAddresses(deliveryAddresses.map(addr => 
        addr.id === id ? { ...addr, ...updatedAddress } : addr
      ));
      console.log('âœ… Address updated successfully!');
    };

    const handleDeleteAddress = async (id) => {
      if (deliveryAddresses.length === 1) {
        showToast('You must have at least one delivery address', 'warning');
        return;
      }
      const addressToDelete = deliveryAddresses.find(addr => addr.id === id);
      if (!window.confirm('Delete "' + (addressToDelete?.label || 'this') + '" address?')) return;

      const response = await apiService.deleteDeliveryAddress(id);
      if (!response.success) {
        showToast(response.data?.message || 'Failed to delete address', 'error');
        return;
      }

      const remaining = deliveryAddresses.filter(addr => addr.id !== id);
      if (addressToDelete?.isDefault && remaining.length > 0) {
        remaining[0].isDefault = true;
      }
      setDeliveryAddresses(remaining);
      showToast('Address deleted', 'success');
    };

    const handleSetDefaultAddress = async (id) => {

      const response = await apiService.setDefaultDeliveryAddress(id);
      if (!response.success) {
        console.log('âŒ Failed to set default address: ' + (response.data?.message || 'Unknown error'));
        return;
      }
      setDeliveryAddresses(deliveryAddresses.map(addr => ({
        ...addr,
        isDefault: addr.id === id
      })));
      console.log('âœ… Default address updated!');
    };
    

    // Add this after line 200 (after existing useEffects)
    useEffect(() => {
      if (isAuthenticated) {
        loadDeliveryAddresses();
      }
    }, [isAuthenticated]);

    // Load favorites from backend when authenticated
    useEffect(() => {
      if (!isAuthenticated) {
        // try to restore from localStorage for unauthenticated users
        try {
          const local = localStorage.getItem('favorites');
          if (local) {
            syncFavoritesState(JSON.parse(local));
          }
        } catch (err) {
          console.error('Failed to parse favorites from localStorage', err);
        }
      }
    }, [isAuthenticated]);

    const fetchWallet = async () => {
      try {
        const resp = await apiService.getWallet();
        if (resp && resp.success) {
          setWalletBalance(parseFloat(resp.data?.wallet?.balance ?? 0));
        }
      } catch (e) {
        console.warn('fetchWallet failed', e);
      }
    };

    // Load wallet when authenticated
    useEffect(() => {
      if (isAuthenticated) {
        fetchWallet();
      } else {
        setWalletBalance(0);
      }
    }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    // â”€â”€ Process PayU callback result once auth is resolved â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    useEffect(() => {
      if (!payuCallbackResult) return;

      const { status, orderId, txnid, reason } = payuCallbackResult;
      const hasToken = !!apiService.getToken();
      const hasOrderId = !!orderId;
      const canFetchOrder = (isAuthenticated || hasToken) && hasOrderId;

      setPayuCallbackResult(null); // consume it so it doesn't fire again

      // Ensure auth modal is hidden during payment result flow
      setShowAuthModal(false);
      if (hasToken && !isAuthenticated) {
        setIsAuthenticated(true);
      }

      if (status === 'success') {
        // Fallback: if auth/session is unavailable right after callback,
        // still show confirmation instead of dropping user into login.
        if (!canFetchOrder) {
          setOrderDetails({
            orderId: orderId || '',
            orderNumber: orderId || '',
            paymentTxnId: txnid || null,
            items: [],
            totalAmount: 0,
            originalAmount: 0,
            couponCode: null,
            couponDiscount: 0,
            shippingCharge: 0,
            taxAmount: 0,
            deliverySlot: deliverySlot || 'morning',
            paymentMethod: paymentMethod || 'upi',
            deliveryAddress: null,
            estimatedDelivery: 'Tomorrow, ' + new Date(Date.now() + 86400000).toLocaleDateString('en-IN', {
              weekday: 'short', month: 'short', day: 'numeric'
            })
          });
          setShowPayment(false);
          setShowCheckout(false);
          setShowOrderConfirmation(true);
          setActiveTab('home');
          showToast('Payment successful! Your order has been confirmed.', 'success');
          return;
        }

        // Fetch order details then show the confirmation modal
        apiService.getOrderById(orderId)
          .then(resp => {
            const order = resp?.data?.order || resp?.data || null;
            // Build delivery address from order shipping fields
            const deliveryAddr = order ? {
              address: order.shipping_address || '',
              city: order.shipping_city || '',
              state: order.shipping_state || '',
              pincode: order.shipping_pincode || '',
            } : null;
            // Normalize order items: API uses product_name, modal expects name
            const rawItems = order?.items || order?.order_items || [];
            const normalizedItems = rawItems.map(item => ({
              ...item,
              name: item.name || item.product_name || item.product?.name || '',
              price: parseFloat(item.price || 0),
              quantity: item.quantity || 1,
            }));
            const confirmationDetails = {
              orderId: order?.id || orderId,
              orderNumber: order?.order_number || orderId,
              paymentTxnId: txnid,
              items: normalizedItems,
              totalAmount: order?.total || 0,
              originalAmount: order?.subtotal || order?.total || 0,
              couponCode: order?.coupon_code || null,
              couponDiscount: order?.coupon_discount || 0,
              shippingCharge: order?.shipping_fee || 0,
              taxAmount: order?.tax || 0,
              deliverySlot: order?.delivery_slot || deliverySlot,
              paymentMethod: order?.payment_method || paymentMethod,
              deliveryAddress: order?.delivery_address || deliveryAddr,
              estimatedDelivery: 'Tomorrow, ' + new Date(Date.now() + 86400000).toLocaleDateString('en-IN', {
                weekday: 'short', month: 'short', day: 'numeric'
              })
            };
            setOrderDetails(confirmationDetails);
            setShowOrderConfirmation(true);
            // Refresh orders list and cart
            loadOrders();
            clearCart();
            handleRemoveCoupon();
            showToast('Payment successful! Your order has been confirmed.', 'success');
          })
          .catch(err => {
            console.error('Failed to fetch order after PayU success:', err);
            // Show a minimal confirmation even if order fetch fails
            setOrderDetails({
              orderId: orderId,
              orderNumber: orderId || '',
              paymentTxnId: txnid,
              items: [],
              totalAmount: 0,
              paymentMethod: paymentMethod,
              estimatedDelivery: 'Tomorrow, ' + new Date(Date.now() + 86400000).toLocaleDateString('en-IN', {
                weekday: 'short', month: 'short', day: 'numeric'
              })
            });
            setShowOrderConfirmation(true);
            showToast('Payment successful! Your order has been confirmed.', 'success');
            loadOrders();
            clearCart();
            handleRemoveCoupon();
          });
      } else {
        const msg = reason
          ? decodeURIComponent(reason).replace(/_/g, ' ')
          : 'Your payment could not be processed';
        showToast(`Payment failed: ${msg}`, 'error');
      }
    }, [payuCallbackResult, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    const formatNotificationRelativeTime = (timestamp) => {
      if (!timestamp) return 'Just now';

      const time = new Date(timestamp).getTime();
      if (Number.isNaN(time)) return 'Just now';

      const diff = Math.max(0, Date.now() - time);
      const minute = 60 * 1000;
      const hour = 60 * minute;
      const day = 24 * hour;

      if (diff < minute) return 'Just now';
      if (diff < hour) return `${Math.floor(diff / minute)} min ago`;
      if (diff < day) return `${Math.floor(diff / hour)} hour${Math.floor(diff / hour) > 1 ? 's' : ''} ago`;
      return `${Math.floor(diff / day)} day${Math.floor(diff / day) > 1 ? 's' : ''} ago`;
    };

    const normalizeNotificationItem = (item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const createdAtRaw = item.created_at || item.createdAt || null;
      const createdAt = createdAtRaw ? new Date(createdAtRaw).toISOString() : new Date().toISOString();
      const unread = item.unread !== undefined ? !!item.unread : !item.read_at;

      return {
        id: item.id || `notification-${Math.random().toString(36).slice(2)}`,
        title: item.title || 'Notification',
        message: item.message || '',
        type: item.type || 'alert',
        unread,
        readAt: item.read_at || item.readAt || null,
        createdAt,
        time: item.time || formatNotificationRelativeTime(createdAt),
        data: item.data || {},
      };
    };

    const loadNotifications = async () => {
      if (!isAuthenticated) {
        setNotifications([]);
        return;
      }

      try {
        const response = await apiService.getNotifications();
        if (!response?.success) {
          console.warn('Failed to load notifications:', response?.data?.message || response?.status);
          return;
        }

        const notificationData = response?.data?.notifications || [];
        const normalized = Array.isArray(notificationData)
          ? notificationData.map(normalizeNotificationItem).filter(Boolean)
          : [];

        setNotifications(normalized);
      } catch (error) {
        console.error('Failed to load notifications:', error);
      }
    };

    const handleMarkNotificationAsRead = async (notificationId) => {
      const response = await apiService.markNotificationRead(notificationId);
      if (!response?.success) {
        showToast('Failed to mark notification as read', 'error');
        return;
      }

      setNotifications((prev) => prev.map((notification) => (
        notification.id === notificationId
          ? {
              ...notification,
              unread: false,
              readAt: response?.data?.notification?.read_at || new Date().toISOString(),
            }
          : notification
      )));
    };

    const handleMarkAllNotificationsAsRead = async () => {
      const response = await apiService.markAllNotificationsRead();
      if (!response?.success) {
        showToast('Failed to mark all notifications as read', 'error');
        return;
      }

      const nowIso = new Date().toISOString();
      setNotifications((prev) => prev.map((notification) => ({
        ...notification,
        unread: false,
        readAt: notification.readAt || nowIso,
      })));
    };

    const handleDeleteNotification = async (notificationId) => {
      const response = await apiService.deleteNotification(notificationId);
      if (!response?.success) {
        showToast('Failed to delete notification', 'error');
        return;
      }

      setNotifications((prev) => prev.filter((notification) => notification.id !== notificationId));
    };

    const handleClearNotifications = async () => {
      const response = await apiService.clearNotifications();
      if (!response?.success) {
        showToast('Failed to clear notifications', 'error');
        return;
      }

      setNotifications([]);
    };

    const handleViewOrderFromNotification = (orderId, orderNumber) => {
      setShowNotificationsModal(false);
      setOrderToOpenFromNotification(orderId || null);
      setActiveTab('orders');

      const orderRef = orderNumber || orderId;
      if (orderRef) {
        showToast(`Opening order #${orderRef}`, 'info');
      }
    };

    const loadFavorites = async () => {
      try {
        setLoading(true);
        if (apiService && typeof apiService.getFavorites === 'function') {
          const firstPage = await apiService.getFavorites(1, 100);
          if (firstPage && firstPage.success) {
            let combinedFavorites = normalizeFavorites(firstPage.data);
            const pagination = firstPage.data?.favorites;
            const lastPage = Number(pagination?.last_page || 1);

            if (Array.isArray(pagination?.data) && lastPage > 1) {
              for (let page = 2; page <= lastPage; page += 1) {
                const pageResponse = await apiService.getFavorites(page, 100);
                if (!pageResponse?.success) {
                  break;
                }

                const nextPageFavorites = normalizeFavorites(pageResponse.data);
                combinedFavorites = normalizeFavorites([...combinedFavorites, ...nextPageFavorites]);
              }
            }

            syncFavoritesState(combinedFavorites);
            return combinedFavorites;
          }
        }
        // fallback - try subscriptionsAPI or localStorage
        if (subscriptionsAPI && typeof subscriptionsAPI.getFavorites === 'function') {
          const resp = await subscriptionsAPI.getFavorites();
          if (resp && resp.success) {
            const fallbackFavorites = normalizeFavorites(resp.data || []);
            syncFavoritesState(fallbackFavorites);
            return fallbackFavorites;
          }
        }

        try {
          const cached = localStorage.getItem('favorites');
          if (cached) {
            syncFavoritesState(JSON.parse(cached));
          }
        } catch (storageError) {
          console.warn('Failed to load cached favorites fallback', storageError);
        }
      } catch (err) {
        console.error('Error loading favorites:', err);
      } finally {
        setLoading(false);
      }
    };

    // Keep selectedAddressId in sync with deliveryAddresses (only real addresses with an address field)
    useEffect(() => {
      const real = Array.isArray(deliveryAddresses)
        ? deliveryAddresses.filter(a => a.address && a.address.trim())
        : [];
      if (real.length === 0) {
        setSelectedAddressId(null);
        return;
      }
      const hasSelected = real.some(a => a.id === selectedAddressId);
      if (!hasSelected) {
        const def = real.find(a => a.isDefault) || real[0];
        setSelectedAddressId(def?.id || null);
      }
    }, [deliveryAddresses]);

    const loadDeliveryAddresses = async () => {
      try {
        setLoading(true);
        const response = await apiService.fetchDeliveryAddresses();

        if (response.success && response.data.addresses && Array.isArray(response.data.addresses) && response.data.addresses.length > 0) {
          setDeliveryAddresses(response.data.addresses);
        } else if (userProfile.address) {
          // If API returned no addresses, or local placeholder exists, replace with profile-derived default
          const defaultAddress = {
            id: 1,
            label: 'Home',
            recipient_name: userProfile.name || 'John Doe',
            address: userProfile.address,
            city: userProfile.city || '',
            state: 'Haryana',
            country: 'India',
            landmark: 'Near City Mall',
            pincode: userProfile.pincode || '',
            isDefault: true,
            phone: userProfile.mobile || mobile,
            latitude: 28.4595,
            longitude: 77.0266
          };

          const hasPlaceholder = Array.isArray(deliveryAddresses) && (deliveryAddresses.length === 0 || !deliveryAddresses[0].address || /Main Street/i.test(deliveryAddresses[0].address));

          if (hasPlaceholder || !(response.success && response.data && response.data.addresses && response.data.addresses.length > 0)) {
            setDeliveryAddresses([defaultAddress]);
          }
        }
      } catch (err) {
        console.error('Failed to load delivery addresses:', err);
      } finally {
        setLoading(false);
      }
    };

    // Refresh addresses and auto-apply first-order coupon every time checkout opens
    useEffect(() => {
      if (showCheckout && isAuthenticated) {
        loadDeliveryAddresses();
        autoApplyFirstOrderCoupon();
      }
    }, [showCheckout]); // eslint-disable-line react-hooks/exhaustive-deps

    const [currentPage, setCurrentPage] = useState(1);
    const [ordersPerPage] = useState(10);
    const [ordersPagination, setOrdersPagination] = useState(null);
    const [orderSearch, setOrderSearch] = useState('');

    const loadOrders = async (page = currentPage, search = orderSearch) => {
      try {
        console.log('ðŸ”„ Loading orders... page:', page, 'isAuthenticated:', isAuthenticated);
        setLoading(true);
        const filters = search ? { search } : {};
        const result = await orderHelpers.getMyOrders(page, ordersPerPage, filters);
        console.log('ðŸ“¦ Orders result:', result);
        
        if (result && typeof result === 'object') {
          // Handle paginated response
          const ordersData = result.orders || result;
          const paginationData = result.pagination || null;
          
          console.log('ðŸ“¦ Orders count:', ordersData?.length);
          console.log('ðŸ“„ Pagination:', paginationData);
          
          setOrders(Array.isArray(ordersData) ? ordersData : []);
          setOrdersPagination(paginationData);
          setCurrentPage(page);
        } else {
          // Fallback for non-paginated response
          setOrders(Array.isArray(result) ? result : []);
        }
      } catch (err) {
        console.error('âŒ Failed to load orders:', err);
        setOrders([]);
        setOrdersPagination(null);
      } finally {
        setLoading(false);
      }
    };

    const handlePageChange = (newPage) => {
      if (newPage >= 1 && (!ordersPagination || newPage <= ordersPagination.total_pages)) {
        loadOrders(newPage, orderSearch);
      }
    };

    const handleOrderSearch = (val) => {
      setOrderSearch(val);
      loadOrders(1, val);
    };

    useEffect(() => {
      console.log('ðŸ“Š Orders useEffect triggered:', { isAuthenticated, activeTab });
      if (isAuthenticated) {
        // Load orders when authenticated and on orders or profile tabs
        if (activeTab === 'orders' || activeTab === 'profile' || activeTab === 'subscriptions') {
          console.log('âœ… Conditions met, calling loadOrders...');
          loadOrders();
        }
      } else {
        console.log('â­ï¸ Skipping loadOrders (not authenticated):', { isAuthenticated, activeTab });
      }
    }, [isAuthenticated, activeTab]);

    // Load orders on initial mount if using cached auth
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      console.log('ðŸš€ Initial orders load check on mount');
      if (isAuthenticated && activeTab === 'profile') {
        console.log('âœ… Loading orders on initial mount');
        loadOrders();
      }
    }, []); // Empty dependency array - runs once on mount

    // Monitor favorites state changes
    useEffect(() => {
      console.log('ðŸ“Š Favorites state updated:', {
        count: favorites ? favorites.length : 0,
        isArray: Array.isArray(favorites),
        favorites: favorites
      });
    }, [favorites]);


    useEffect(() => {
      if (!isAuthenticated) {
        return;
      }

      if (activeTab === 'home' || activeTab === 'profile' || showFavorites) {
        loadFavorites();
      }
    }, [isAuthenticated, activeTab, showFavorites]);

    useEffect(() => {
      loadNotifications();
    }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (!isAuthenticated) {
        return undefined;
      }

      const currentUser = apiService.getUser();
      const userId = Number(currentUser?.id);

      if (!Number.isFinite(userId) || userId <= 0) {
        return undefined;
      }

      const unsubscribe = subscribeToUserNotifications(userId, (incomingNotification) => {
        const normalized = normalizeNotificationItem(incomingNotification);
        if (!normalized) {
          return;
        }

        setNotifications((prev) => {
          const deduped = [normalized, ...prev.filter((item) => String(item.id) !== String(normalized.id))];
          return deduped.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        });

        if (normalized.title) {
          showToast(normalized.title, 'info');
        }
      });

      return () => {
        unsubscribe();
      };
    }, [isAuthenticated, showToast]);

    // Load favorites on initial mount if using cached auth
      // Subscribe to public promotion/coupon broadcasts (no auth required)
      useEffect(() => {
        const unsubscribe = subscribeToPromotions((payload) => {
          const promoNotification = {
            id: `promo-${Date.now()}`,
            title: payload.title || 'New Promotion',
            message: payload.message || '',
            type: payload.type || 'promotion',
            unread: true,
            readAt: null,
            createdAt: new Date().toISOString(),
            time: 'Just now',
            data: payload.data || {},
          };
          setNotifications((prev) => [promoNotification, ...prev]);
          showToast(payload.title || 'New Promotion', 'info');
        });
        return () => { unsubscribe(); };
      }, [showToast]); // eslint-disable-line react-hooks/exhaustive-deps

      // Subscribe to public app-update broadcasts
      useEffect(() => {
        const unsubscribe = subscribeToAppUpdates((payload) => {
          setAppUpdateInfo(payload);

          const appUpdateNotification = {
            id: `app-update-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            title: payload?.title || 'App Update Available',
            message: payload?.message || '',
            type: 'app_update',
            unread: true,
            readAt: null,
            createdAt: new Date().toISOString(),
            time: 'Just now',
            data: {
              version: payload?.version || null,
              release_notes: payload?.release_notes || [],
              force_update: !!payload?.force_update,
            },
          };

          setNotifications((prev) => [appUpdateNotification, ...prev]);

          // Auto-logout after 3 seconds so the user sees the update banner
          setTimeout(async () => {
            try {
              await apiService.logout();
            } catch (_) {
              apiService.removeToken({ clearAppState: true, clearAllStorage: true });
            }
            setIsAuthenticated(false);
            setShowAuthModal(true);
            setShowLogoutConfirm(false);
            setAuthStep('phone');
            setMobile('');
            setOtp(['', '', '', '', '', '']);
            setPassword('');
            setAppUpdateInfo(null);
          }, 3000);
        });
        return () => { unsubscribe(); };
      }, []); // eslint-disable-line react-hooks/exhaustive-deps

      // Load favorites on initial mount if using cached auth
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      console.log('ðŸš€ Initial favorites load check on mount');
      if (isAuthenticated && (activeTab === 'home' || activeTab === 'profile')) {
        console.log('âœ… Loading favorites on initial mount');
        loadFavorites();
      }
    }, []); // Empty dependency array - runs once on mount

    useEffect(() => {
      if (isAuthenticated) {
        // Load cart from backend after authentication
        loadCart();
      }
    }, [isAuthenticated]);

    // Load subscription from localStorage on startup and try to sync with server when authenticated
    const loadSubscriptions = async () => {
      // Load local subscription first so UI reflects it immediately
      try {
        const local = localStorage.getItem('subscription');
        if (local) {
          const parsed = JSON.parse(local);
          setSubscription(parsed);
        }
      } catch (err) {
        console.warn('Failed to parse local subscription:', err);
      }

      // If authenticated, try to fetch subscriptions from server and prefer server copy
      if (!isAuthenticated) return;

      try {
        setLoading(true);
        let resp;
        // Prefer apiService if available
        if (apiService && typeof apiService.getMySubscriptions === 'function') {
          resp = await apiService.getMySubscriptions();
        } 

        if (resp && resp.success) {
          // Backend returns { subscriptions: { data: [...], ... } } (Laravel paginator)
          const raw  = resp.data?.subscriptions?.data
                    || (Array.isArray(resp.data?.subscriptions) ? resp.data.subscriptions : null)
                    || resp.data?.data
                    || resp.data
                    || [];
          const subs = Array.isArray(raw) ? raw : [];
          if (subs.length > 0) {
            setAllSubscriptions(subs);
            const active = subs.find(s => s.status === 'active') || subs[0];
            setSubscription(active);
            localStorage.setItem('subscription', JSON.stringify(active));
          }
        }
      } catch (err) {
        console.warn('Could not load subscriptions from server:', err);
      } finally {
        setLoading(false);
      }
    };

    const loadReferralInfo = async () => {
      if (!isAuthenticated) return;
      try {
        const resp = await apiService.getReferralInfo();
        if (resp && resp.success) setReferralInfo(resp.data);
      } catch (err) {
        console.warn('Could not load referral info:', err);
      }
    };

    useEffect(() => {
      if (isAuthenticated && activeTab === 'profile') loadReferralInfo();
    }, [isAuthenticated, activeTab]);

    useEffect(() => {
      // Load local subscription first so UI reflects it immediately
      loadSubscriptions();
    }, [isAuthenticated]);

    // Scroll to top when tab changes
    useEffect(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [activeTab]);
    
    
  // This is a simplified version - full App.js continues with all screens and modals
  // Due to length limits, components are separated in other artifacts
  
  // Products eligible for subscription
  const subscribableProducts = Array.isArray(productsList)
    ? productsList.filter(p => Boolean(Number(p.is_subscribable)))
    : [];

  // Calculate filtered products inside component
  const filteredProducts = Array.isArray(productsList)
    ? productsList.filter(p => {
        // Filter out inactive products (check various field names)
        const isActive = p.status !== 'inactive' && 
                        p.status !== false && 
                        p.is_active !== false && 
                        p.isActive !== false && 
                        p.active !== false;
        
        const matchesSearch = (p.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                            (typeof p.category === 'object' ? (p.category?.name?.toLowerCase() || '') : (p.category?.toLowerCase() || '')).includes(searchQuery.toLowerCase());
        
        const categoryName = typeof p.category === 'object' && p.category?.name ? p.category.name : p.category;
        const matchesCategory = selectedCategory === 'All' || categoryName === selectedCategory;

        const activeCategoryNames = categories.map(c => c.name);
        const inActiveCategory = categories.length === 0 || activeCategoryNames.includes(categoryName);

        return isActive && matchesSearch && matchesCategory && inActiveCategory;
      })
    : [];

  const loaderLogoSrc = `${process.env.PUBLIC_URL || ''}/logo.png`;

  if (authBootstrapPending) {
    return (
      <div className="min-h-screen bg-white md:flex md:justify-center">
        <div className="w-full max-w-md mx-auto bg-gray-50 min-h-screen flex flex-col items-center justify-center gap-4 md:rounded-3xl md:shadow-2xl md:overflow-hidden md:min-h-0 md:self-start md:mb-6 md:py-16">
          <div className="relative w-20 h-20">
            <div className="absolute inset-0 rounded-full border-4 border-blue-100 border-t-blue-600 dealstore-loader-ring" />
            <div className="absolute inset-2 rounded-2xl bg-white shadow-lg flex items-center justify-center dealstore-soft-float">
              <img
                src={loaderLogoSrc}
                alt="DealStore logo"
                className="w-10 h-10 object-contain"
              />
            </div>
          </div>
          <div className="text-center px-6">
            <p className="text-base font-semibold text-gray-800">Restoring your session</p>
           </div>
        </div>
      </div>
    );
  }
  
  // Compute theme class for main container
  const themeClass = `theme-${appTheme}`;

  // On desktop, the Home tab renders a full-width layout instead of the mobile screen.
  // Other tabs render inside a shared desktop shell (navbar + centered content + footer).
  const HomeComponent = isDesktop ? DesktopHomeScreen : HomeScreen;
  // Full-width desktop layout for the app (home is browsable by guests too).
  // The login overlay centers itself via CSS, so it stays centered even here.
  const desktopOther = isDesktop && activeTab !== 'home';

  // Single shared desktop navbar node — injected into every desktop page/overlay
  // so the header links work everywhere. Uses navigate() to clear overlays on click.
  const renderDesktopNavbar = () => (
    <DesktopNavbar
      appSettings={appSettings}
      userProfile={userProfile}
      activeTab={activeTab}
      setActiveTab={navigate}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      selectedLocation={selectedLocation}
      setShowLocationModal={setShowLocationModal}
      notifications={notifications}
      favorites={Array.isArray(favorites) ? favorites : []}
      getCartCount={getCartCount}
      setShowNotifications={openNotifications}
      setShowFavorites={openFavorites}
      handleLogout={handleLogout}
      isAuthenticated={isAuthenticated}
      onLogin={() => setShowAuthModal(true)}
    />
  );

  // Expose setActiveTab globally for modals (e.g., PhoneLoginModal)
  // (Merged into main window global useEffect above)

  return (
    <div className={`min-h-screen bg-white ${isDesktop ? '' : 'md:flex md:justify-center'} ${themeClass}`}>
      <CookieConsent />
      <div className={`bg-gray-50 min-h-screen relative ${isDesktop ? 'w-full' : 'w-full max-w-md mx-auto md:rounded-3xl md:shadow-2xl md:overflow-hidden md:min-h-0 md:self-start md:mb-6'}`}>
        {/* PWA Components */}
        <OfflineIndicator appTheme={appTheme} />
        <PWAInstallPrompt showToast={showToast} />

        {/* Toast Notification - Hidden during authentication */}
        {toast && !showAuthModal && (
          <Toast
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={hideToast}
          />
        )}

        {/* Error Toast Notification */}
        {error && !showAuthModal && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] w-full max-w-md px-4">
            <div className="bg-red-500 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center justify-between animate-slide-down">
              <div className="flex items-center gap-3">
                <span className="text-2xl">⚠️</span>
                <div>
                  <p className="font-semibold">Error</p>
                  <p className="text-sm">{error}</p>
                </div>
              </div>
              <button 
                onClick={() => setError(null)}
                className="text-white hover:bg-red-600 p-2 rounded-lg transition-all"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Always allow Terms and Privacy Policy pages to be viewed, even if not authenticated.
            When authenticated, these render inside the desktop shell below instead. */}
        {!isAuthenticated && activeTab === 'terms' && (
          <TermsScreen setActiveTab={setActiveTab} onBack={goBack} appTheme={appTheme} />
        )}
        {!isAuthenticated && activeTab === 'privacy' && (
          <PrivacyPolicyScreen setActiveTab={setActiveTab} onBack={goBack} appTheme={appTheme} />
        )}

        {/* Main app content. Home and public pages render for guests too;
            protected tabs below are individually gated on isAuthenticated.
            While an auth screen is open, hide the app so login is a full page. */}
        {!(showAuthModal || showSignUp || showForgotPassword) && (
          <>
          {activeTab === 'home' && (
              <HomeComponent
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onNavigate={navigate}
                isAuthenticated={isAuthenticated}
                onLogin={() => setShowAuthModal(true)}
                setShowNotifications={openNotifications}
                setShowLocationModal={setShowLocationModal}
                showLocationModal={showLocationModal}
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                currentBanner={currentBanner}
                setCurrentBanner={setCurrentBanner}
                addToCart={addToCart}
                setShowSubscription={openSubscription}
                products={productsList}
                filteredProducts={filteredProducts}
                cart={cart}
                getCartCount={getCartCount}
                getCartTotal={getCartTotal}
                updateQuantity={updateQuantity}
                openCheckout={openCheckout}
                userProfile={userProfile}
                notifications={notifications}
                favorites={Array.isArray(favorites) ? favorites : []}
                setShowFavorites={openFavorites}
                setFavorites={setFavorites}
                toggleFavorite={toggleFavorite}
                setActiveTab={navigate}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                categories={categories}
                onCategoryClick={(catObj) => setCategoryPageView(catObj)}
                onOpenOffers={(sectionId) => setOffersPage(sectionId || 'all')}
                showHeader={!categoryPageView && !offersPage && !showSubscription && !showOrderConfirmation && !showFavorites && !showNotificationsModal && !showLogoutConfirm && !(showAuthModal && (authStep === 'whatsapp' || authStep === 'profile'))}
                showToast={showToast}
                appSettings={appSettings}
                handleLogout={handleLogout}
                featureSections={featureSections}
              />
            )}

          {/* Category Products Page — full-screen overlay when a category is selected */}
          {categoryPageView && (
            <CategoryProductsScreen
              category={categoryPageView}
              onBack={() => setCategoryPageView(null)}
              cart={cart}
              addToCart={addToCart}
              updateQuantity={updateQuantity}
              getCartCount={getCartCount}
              getCartTotal={getCartTotal}
              openCheckout={openCheckout}
              toggleFavorite={toggleFavorite}
              favorites={Array.isArray(favorites) ? favorites : []}
              setShowSubscription={setShowSubscription}
              showToast={showToast}
              appTheme={appTheme}
              isDesktop={isDesktop}
              headerSlot={isDesktop ? renderDesktopNavbar() : null}
            />
          )}

          {/* Offers / Deals page overlay */}
          {offersPage && (
            <OffersScreen
              initialSectionId={offersPage}
              onBack={() => setOffersPage(null)}
              cart={cart}
              addToCart={addToCart}
              updateQuantity={updateQuantity}
              getCartCount={getCartCount}
              getCartTotal={getCartTotal}
              openCheckout={openCheckout}
              toggleFavorite={toggleFavorite}
              favorites={Array.isArray(favorites) ? favorites : []}
              setShowSubscription={setShowSubscription}
              showToast={showToast}
              isDesktop={isDesktop}
              headerSlot={isDesktop ? renderDesktopNavbar() : null}
            />
          )}

          {/* Shared desktop chrome for all non-home tabs */}
          {desktopOther && renderDesktopNavbar()}

          <div className={desktopOther ? 'max-w-5xl mx-auto w-full px-6 py-6 min-h-[60vh]' : ''}>
          {isAuthenticated && activeTab === 'cart' && (<CartScreen
            cart={cart}
            updateQuantity={updateQuantity}
            removeFromCart={removeFromCart}
            getCartTotal={getCartTotal}
            handleCheckout={openCheckout}
            getCartCount={getCartCount}
            couponCode={couponCode}
            setCouponCode={setCouponCode}
            appliedCoupon={appliedCoupon}
            couponDiscount={couponDiscount}
            couponError={couponError}
            handleApplyCoupon={handleApplyCoupon}
            handleRemoveCoupon={handleRemoveCoupon}
            getFinalTotal={getFinalTotal}
            getSuggestedCoupons={getSuggestedCoupons}
            appSettings={appSettings}
            setActiveTab={setActiveTab}
            goBack={goBack}
            appTheme={appTheme}
            isDesktop={isDesktop}
          />)}
          {isAuthenticated && activeTab === 'orders' && (<OrdersScreen
            orders={orders}
            loading={loading}
            loadOrders={loadOrders}
            pagination={ordersPagination}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            addToCart={addToCart}
            showToast={showToast}
            loadCart={loadCart}
            setActiveTab={setActiveTab}
            highlightedOrderId={orderToOpenFromNotification}
            onHighlightedOrderHandled={() => setOrderToOpenFromNotification(null)}
            appTheme={appTheme}
            orderSearch={orderSearch}
            onSearchChange={handleOrderSearch}
            goBack={goBack}
            upiQrCode={appSettings.upiQrCode}
            upiId={appSettings.upiId}
            onOrderCancelled={fetchWallet}
            isDesktop={isDesktop}
          /> )}

          {isAuthenticated && activeTab === 'subscriptions' && (
            <SubscriptionsScreen
              allSubscriptions={allSubscriptions}
              loading={loading}
              loadSubscriptions={loadSubscriptions}
              handlePauseSubscription={pauseSubscription}
              handleResumeSubscription={resumeSubscription}
              handleCancelSubscription={cancelSubscription}
              handleModifySubscription={handleModifySubscription}
              handleReactivateSubscription={reactivateSubscription}
              appTheme={appTheme}
              setActiveTab={setActiveTab}
              goBack={goBack}
              isDesktop={isDesktop}
              addToCart={addToCart}
              showToast={showToast}
              toggleFavorite={toggleFavorite}
              favorites={Array.isArray(favorites) ? favorites : []}
              setShowSubscription={openSubscription}
            />
          )}

          {isAuthenticated && activeTab === 'profile' && (<ProfileScreen
            userProfile={userProfile}
            setUserProfile={setUserProfile}
            handleLogout={handleLogout}
            subscription={subscription}
            setSubscription={setSubscription}
            twoFactorEnabled={twoFactorEnabled}
            setTwoFactorEnabled={setTwoFactorEnabled}
            showSecuritySettings={showSecuritySettings}
            setShowSecuritySettings={setShowSecuritySettings}
            loginHistory={loginHistory}
            securityLogs={securityLogs}
            orders={orders}
            favorites={favorites}
            setFavorites={setFavorites}
            setShowFavorites={setShowFavorites}
            setShowNotificationsModal={setShowNotificationsModal}
            setShowPaymentMethods={setShowPaymentMethods}
            setShowEditAddress={setShowEditAddress}
            setShowEditProfile={setShowEditProfile}
            addSecurityLog={addSecurityLog}
            deliveryAddresses={deliveryAddresses}
            handleAddAddress={handleAddAddress}
            handleUpdateAddress={handleUpdateAddress}
            handleDeleteAddress={handleDeleteAddress}
            handleSetDefaultAddress={handleSetDefaultAddress}
            setShowAddAddress={setShowAddAddress} 
            editingAddress={editingAddress}         
            setEditingAddress={setEditingAddress} 
            allSubscriptions={allSubscriptions}
            handlePauseSubscription={pauseSubscription}
            handleResumeSubscription={resumeSubscription}
            handleCancelSubscription={cancelSubscription}
            handleModifySubscription={handleModifySubscription}
            handleReactivateSubscription={reactivateSubscription}
            appSettings={appSettings}
            appTheme={appTheme}
            onChangeTheme={handleThemeChange}
            setActiveTab={setActiveTab}
            goBack={goBack}
            walletBalance={walletBalance}
            onShowWalletTopup={() => setShowWalletTopup(true)}
            onShowWalletTransactions={() => setShowWalletTransactions(true)}
            referralInfo={referralInfo}
            onApplyReferralCode={(code, orderId) => apiService.applyReferralCode(code, orderId)}
            isDesktop={isDesktop}
            />)}



          {/* Always allow Terms and Privacy Policy pages to be viewed, even if not authenticated */}
          {activeTab === 'about' && (
            <AboutUsScreen appSettings={appSettings} setActiveTab={setActiveTab} onBack={goBack} appTheme={appTheme} isDesktop={isDesktop} />
          )}
          {activeTab === 'terms' && (
            <TermsScreen setActiveTab={setActiveTab} onBack={goBack} appTheme={appTheme} isDesktop={isDesktop} />
          )}
          {activeTab === 'privacy' && (
            <PrivacyPolicyScreen setActiveTab={setActiveTab} onBack={goBack} appTheme={appTheme} isDesktop={isDesktop} />
          )}
          {activeTab === 'cms-page' && (
            <CmsPageScreen
              slug={cmsPageSlug}
              pageTitle={cmsPageTitle}
              onBack={goBack}
              setActiveTab={setActiveTab}
              appTheme={appTheme}
              appSettings={appSettings}
            />
          )}

          {activeTab === 'help' && (
            <HelpSupportScreen
              showToast={showToast}
              appSettings={appSettings}
              setActiveTab={setActiveTab}
              goBack={goBack}
              appTheme={appTheme}
              isDesktop={isDesktop}
            />
          )}
          </div>

          {desktopOther && (
            <DesktopFooter
              appSettings={appSettings}
              setActiveTab={navigate}
              setShowFavorites={openFavorites}
              setSelectedCategory={setSelectedCategory}
              onOpenOffers={(id) => setOffersPage(id || 'all')}
            />
          )}

          {showCheckout && (<CheckoutModal
            cart={cart}
            getCartTotal={getCartTotal}
            deliverySlots={deliverySlots}
            selectedSlot={deliverySlot}
            setSelectedSlot={setDeliverySlot}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            onClose={() => setShowCheckout(false)}
            onModifyCart={() => { setShowCheckout(false); setActiveTab('cart'); }}
            processPayment={processPayment}
            appliedCoupon={appliedCoupon}
            couponDiscount={couponDiscount}
            couponCode={couponCode}
            setCouponCode={setCouponCode}
            couponError={couponError}
            handleApplyCoupon={handleApplyCoupon}
            handleRemoveCoupon={handleRemoveCoupon}
            getSuggestedCoupons={getSuggestedCoupons}
            getFinalTotal={getFinalTotal}
            deliveryAddresses={deliveryAddresses}
            setShowAddAddress={setShowAddAddress}
            handleSetDefaultAddress={handleSetDefaultAddress}
            selectedAddressId={selectedAddressId}
            setSelectedAddressId={setSelectedAddressId}
            appSettings={appSettings}
            walletBalance={walletBalance}
            onWalletUsed={(amount) => setWalletBalance(prev => Math.max(0, prev - amount))}
            upiQrCode={appSettings.upiQrCode}
            upiId={appSettings.upiId}
          />)}

          {showPayment && (
            <PaymentModal
              isOpen={showPayment}
              onClose={() => {
                setShowPayment(false);
                setShowCheckout(true);
              }}
              paymentMethod={paymentMethod}
              paymentGateway={paymentMethod === 'razorpay' ? 'razorpay' : 'payu'}
              totalAmount={getFinalTotal()}
              orderId={pendingOrderId}
              userPhone={userProfile.mobile}
              onPaymentComplete={handlePaymentComplete}
              payuEnabled={appSettings.payuEnabled}
              razorpayEnabled={appSettings.razorpayEnabled}
              codEnabled={appSettings.codEnabled}
              upiQrCode={appSettings.upiQrCode}
              upiId={appSettings.upiId}
              razorpayKey={appSettings.razorpay_key}
            />
          )}

          {showOrderConfirmation && (
            <div className={`fixed inset-0 z-[200] bg-slate-50 page-overlay-scroll ${isDesktop ? 'desktop-scrollbar' : ''}`}>
              <div className={`w-full mx-auto min-h-full pb-24 ${isDesktop ? 'max-w-2xl' : 'max-w-md'}`}>
                <div className="px-5 pt-4">
                  <button
                    onClick={() => {
                      setShowOrderConfirmation(false);
                      setShowCheckout(false);
                      setActiveTab('home');
                    }}
                    className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                  >
                    &lt; Back
                  </button>
                </div>
                <OrderConfirmationModal
                  isOpen={showOrderConfirmation}
                  orderDetails={orderDetails}
                  onViewOrders={() => {
                    setShowOrderConfirmation(false);
                    setActiveTab('orders');
                    setShowCheckout(false);
                  }}
                  onContinueShopping={() => {
                    setShowOrderConfirmation(false);
                    setShowCheckout(false);
                    setActiveTab('home');
                  }}
                  onContactSupport={() => {
                    setShowOrderConfirmation(false);
                    setShowCheckout(false);
                    setActiveTab('help');
                  }}
                  upiQrCode={appSettings.upiQrCode}
                  upiId={appSettings.upiId}
                  deliverySlots={deliverySlots}
                />
              </div>
            </div>
          )}

        <LogoutConfirmationModal
          isOpen={showLogoutConfirm}
          onConfirm={confirmLogout}
          onCancel={() => setShowLogoutConfirm(false)}
        />

        {showWalletTopup && (
          <WalletTopupModal
            isOpen={showWalletTopup}
            onClose={() => setShowWalletTopup(false)}
            onSuccess={(newBalance) => {
              setWalletBalance(newBalance);
              setShowWalletTopup(false);
            }}
            appSettings={appSettings}
            razorpayKey={appSettings?.razorpay_key}
          />
        )}

        {showWalletTransactions && (
          <WalletTransactionsModal
            isOpen={showWalletTransactions}
            onClose={() => setShowWalletTransactions(false)}
            walletBalance={walletBalance}
          />
        )}

        {showSubscription && (
          <div className="fixed inset-0 z-[200] bg-slate-50 page-overlay-scroll">
            <div className="w-full max-w-md mx-auto min-h-full">
              <SubscriptionModal
                isOpen={!!showSubscription}
                onClose={() => { setShowSubscription(null); setEditingSubscription(null); }}
                subscriptionPlans={subscriptionPlans}
                showSubscription={showSubscription}
                subscribableProducts={subscribableProducts}
                subscribeProduct={subscribeProduct}
                editSubscription={editingSubscription}
                updateSubscriptionPlan={updateSubscriptionPlan}
                isLoading={loading}
                walletBalance={walletBalance}
                razorpayEnabled={!!appSettings.razorpayEnabled}
              />
            </div>
          </div>
        )}
          {showLocationModal && (<LocationModal
            isOpen={showLocationModal}
            locations={locations}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            onClose={() => setShowLocationModal(false)}
            appTheme={appTheme}
          />)}

          {showMyOrders && (<MyOrdersModal
            isOpen={showMyOrders}
            onClose={() => setShowMyOrders(false)}
            orders={orders}
            addToCart={addToCart}
            showToast={showToast}
            loadCart={loadCart}
          />)}

          {showFavorites && (
            <div className={`fixed inset-0 z-[220] bg-slate-50 page-overlay-scroll ${isDesktop ? 'desktop-scrollbar' : ''}`}>
              {isDesktop && renderDesktopNavbar()}
              <div className={`w-full mx-auto min-h-full ${isDesktop ? 'max-w-3xl px-4 pt-4' : 'max-w-md'}`}>
                <FavoritesModal
                  isOpen={showFavorites}
                  onClose={() => setShowFavorites(false)}
                  favorites={favorites}
                  setFavorites={setFavorites}
                  toggleFavorite={toggleFavorite}
                  products={productsList}
                  addToCart={addToCart}
                  showToast={showToast}
                  appTheme={appTheme}
                  isDesktop={isDesktop}
                  setShowSubscription={openSubscription}
                />
              </div>
            </div>
          )}

          {showNotificationsModal && (
            <div className={`fixed inset-0 z-[220] bg-slate-50 page-overlay-scroll ${isDesktop ? 'desktop-scrollbar' : ''}`}>
              {isDesktop && renderDesktopNavbar()}
              <div className={`w-full mx-auto min-h-full ${isDesktop ? 'max-w-3xl px-4 pt-4' : 'max-w-md'}`}>
                <NotificationsModal
                  isOpen={showNotificationsModal}
                  onClose={() => setShowNotificationsModal(false)}
                  notifications={notifications}
                  setNotifications={setNotifications}
                  onMarkAsRead={handleMarkNotificationAsRead}
                  onMarkAllAsRead={handleMarkAllNotificationsAsRead}
                  onDeleteNotification={handleDeleteNotification}
                  onClearAll={handleClearNotifications}
                  onViewOrder={handleViewOrderFromNotification}
                  appTheme={appTheme}
                  isDesktop={isDesktop}
                />
              </div>
            </div>
          )}

            {appUpdateInfo && (
              <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 flex items-center justify-between shadow-lg">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xl flex-shrink-0">ðŸš€</span>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm leading-tight">{appUpdateInfo.title || 'App Updated'}</p>
                    <p className="text-xs text-blue-100 truncate">{appUpdateInfo.message}</p>
                  </div>
                  {appUpdateInfo.version && (
                    <span className="flex-shrink-0 bg-white bg-opacity-20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      v{appUpdateInfo.version}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  {!appUpdateInfo.force_update && (
                    <button
                      onClick={() => setAppUpdateInfo(null)}
                      className="text-blue-200 hover:text-white text-xs underline"
                    >
                      Later
                    </button>
                  )}
                  <button
                    onClick={() => window.location.reload()}
                    className="bg-white text-blue-600 text-xs font-bold px-3 py-1 rounded-full hover:bg-blue-50"
                  >
                    Update
                  </button>
                </div>
              </div>
            )}

          {showPaymentMethods && (
            <PaymentMethodsModal
              isOpen={showPaymentMethods}
              onClose={() => setShowPaymentMethods(false)}
              paymentMethods={paymentMethods}
              setPaymentMethods={setPaymentMethods}
              showToast={showToast}
              upiQrCode={appSettings.upiQrCode}
              upiId={appSettings.upiId}
            />
          )}

          {showSecuritySettings && (
            <SecuritySettingsModal
              setShowSecuritySettings={setShowSecuritySettings}
              sessionTimeout={sessionTimeout}
              setSessionTimeout={setSessionTimeout}
              loginHistory={loginHistory}
              logoutAllDevices={logoutAllDevices}
              logoutDevice={logoutDevice}
              securityLogs={securityLogs}
            />
          )}

          {showEditAddress && (
            <div className="fixed inset-0 z-[200] bg-slate-50 page-overlay-scroll">
              <div className="w-full max-w-md mx-auto min-h-full">
                <div className="px-5 pt-4">
                  <button
                    onClick={() => setShowEditAddress(false)}
                    className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                  >
                    &lt; Back
                  </button>
                </div>
                <EditAddressModal
                  isOpen={showEditAddress}
                  onClose={() => setShowEditAddress(false)}
                  userProfile={userProfile}
                  setUserProfile={setUserProfile}
                  searchAddress={searchAddress}
                  addressSuggestions={addressSuggestions}
                  showAddressSuggestions={showAddressSuggestions}
                  selectAddress={selectAddress}
                  showToast={showToast}
                />
              </div>
            </div>
          )}
          {showEditProfile && (
          <EditProfileModal
            isOpen={showEditProfile}
            onClose={() => setShowEditProfile(false)}
            userProfile={userProfile}
            setUserProfile={setUserProfile}
            handleProfilePicUpload={handleProfilePicUpload}
            showToast={showToast}
          />
        )}
        {showAddAddress && (
          <div className="fixed inset-0 z-[400] bg-slate-50 page-overlay-scroll">
            <div className="w-full max-w-md mx-auto min-h-full pb-24">
              <div className="px-5 pt-4">
                <button
                  onClick={() => {
                    setShowAddAddress(false);
                    setEditingAddress(null);
                  }}
                  className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                >
                  &lt; Back
                </button>
              </div>
              <AddAddressModal
                isOpen={showAddAddress}
                onClose={() => {
                  setShowAddAddress(false);
                  setEditingAddress(null); // Reset on close
                }}
                onSave={handleAddAddress}
                editAddress={editingAddress} // Pass the address to edit
              />
            </div>
          </div>
        )}
          {!isDesktop && !(showAuthModal && (authStep === 'whatsapp' || authStep === 'profile')) && !showOrderConfirmation && !showPaymentMethods && !showFavorites && !showNotificationsModal && !showAddAddress && !showEditAddress && (
          <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto border-t border-gray-200 bg-white z-[210]" style={{ boxShadow: '0 -2px 16px rgba(0,0,0,0.07)' }}>
            <div className="flex justify-around py-2">
              <button
                onClick={() => navigate('home')}
                className={`flex flex-col items-center px-3 py-1 rounded-xl transition-all ${activeTab === 'home' ? 'text-orange-500' : 'text-gray-400'}`}
              >
                {activeTab === 'home' && <span className="absolute w-5 h-0.5 rounded-full bg-orange-500 -top-0.5" />}
                <Home size={22} strokeWidth={activeTab === 'home' ? 2.5 : 1.8} />
                <span className="text-[11px] mt-0.5 font-medium">Home</span>
              </button>

              <button
                onClick={() => navigate('cart')}
                className={`flex flex-col items-center relative px-3 py-1 rounded-xl transition-all ${activeTab === 'cart' ? 'text-orange-500' : 'text-gray-400'}`}
              >
                {activeTab === 'cart' && <span className="absolute w-5 h-0.5 rounded-full bg-orange-500 -top-0.5" />}
                <ShoppingCart size={22} strokeWidth={activeTab === 'cart' ? 2.5 : 1.8} />
                {cart.length > 0 && getCartCount() > 0 && (
                  <span className="absolute top-0 right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                    {getCartCount()}
                  </span>
                )}
                <span className="text-[11px] mt-0.5 font-medium">Cart</span>
              </button>

              <button
                onClick={() => navigate('orders')}
                className={`flex flex-col items-center px-3 py-1 rounded-xl transition-all ${activeTab === 'orders' ? 'text-orange-500' : 'text-gray-400'}`}
              >
                {activeTab === 'orders' && <span className="absolute w-5 h-0.5 rounded-full bg-orange-500 -top-0.5" />}
                <Package size={22} strokeWidth={activeTab === 'orders' ? 2.5 : 1.8} />
                <span className="text-[11px] mt-0.5 font-medium">Orders</span>
              </button>

              <button
                onClick={() => navigate('profile')}
                className={`flex flex-col items-center px-3 py-1 rounded-xl transition-all ${activeTab === 'profile' ? 'text-orange-500' : 'text-gray-400'}`}
              >
                {activeTab === 'profile' && <span className="absolute w-5 h-0.5 rounded-full bg-orange-500 -top-0.5" />}
                <User size={22} strokeWidth={activeTab === 'profile' ? 2.5 : 1.8} />
                <span className="text-[11px] mt-0.5 font-medium">Profile</span>
              </button>

              <button
                onClick={() => navigate('about')}
                className={`flex flex-col items-center px-3 py-1 rounded-xl transition-all ${activeTab === 'about' ? 'text-orange-500' : 'text-gray-400'}`}
              >
                {activeTab === 'about' && <span className="absolute w-5 h-0.5 rounded-full bg-orange-500 -top-0.5" />}
                <Info size={22} strokeWidth={activeTab === 'about' ? 2.5 : 1.8} />
                <span className="text-[11px] mt-0.5 font-medium">About Us</span>
              </button>
            </div>
          </div>
          )}
        </>
      )}

      {!isAuthenticated && showOrderConfirmation && (
        <div className={`fixed inset-0 z-[200] bg-slate-50 page-overlay-scroll ${isDesktop ? 'desktop-scrollbar' : ''}`}>
          <div className={`w-full mx-auto min-h-full pb-24 ${isDesktop ? 'max-w-2xl' : 'max-w-md'}`}>
            <div className="px-5 pt-4">
              <button
                onClick={() => {
                  setShowOrderConfirmation(false);
                  setShowCheckout(false);
                  setActiveTab('home');
                  if (!apiService.getToken()) {
                    setShowAuthModal(true);
                  }
                }}
                className="text-sm font-semibold text-blue-600 hover:text-blue-700"
              >
                &lt; Back
              </button>
            </div>
            <OrderConfirmationModal
              isOpen={showOrderConfirmation}
              orderDetails={orderDetails}
              onViewOrders={() => {
                setShowOrderConfirmation(false);
                setShowCheckout(false);
                if (apiService.getToken()) {
                  setIsAuthenticated(true);
                  setActiveTab('orders');
                } else {
                  setActiveTab('home');
                  setShowAuthModal(true);
                }
              }}
              onContinueShopping={() => {
                setShowOrderConfirmation(false);
                setShowCheckout(false);
                setActiveTab('home');
                if (!apiService.getToken()) {
                  setShowAuthModal(true);
                }
              }}
              onContactSupport={() => {
                setShowOrderConfirmation(false);
                setShowCheckout(false);
                setActiveTab('help');
                if (!apiService.getToken()) {
                  setShowAuthModal(true);
                }
              }}
              deliverySlots={deliverySlots}
            />
          </div>
        </div>
      )}

       {!authBootstrapPending && showAuthModal && !showForgotPassword && !showSignUp && activeTab !== 'terms' && activeTab !== 'privacy' && (
       <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          authStep={authStep}
          setAuthStep={setAuthStep}
          loginMethod={loginMethod}
          setLoginMethod={setLoginMethod}
          mobile={mobile}
          setMobile={setMobile}
          whatsAppNumber={whatsAppNumber}
          setWhatsAppNumber={setWhatsAppNumber}
          sendOTP={sendOTP}
          sendOTPForLogin={sendOTPForLogin}
          //sendOTPForLogin={sendOTP}
          otp={otp}
          handleOtpChange={handleOtpChange}
          handleOtpKeyDown={handleOtpKeyDown}
          handleFullOtpChange={handleFullOtpChange}
          otpMethod={otpMethod}
          timer={timer}
          canResend={canResend}
          //resendOTP={resendOTP}
          resendOTP={sendOTPForLogin}
          verifyOTP={verifyOTP}
          password={password}
          checkPasswordStrength={checkPasswordStrength}
          passwordStrength={passwordStrength}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          handleEmailLogin={handleEmailLogin}
          rememberMe={rememberMe}
          setRememberMe={setRememberMe}
          handleSocialLogin={handleSocialLogin}
          socialMediaLoginEnabled={appSettings.socialMediaLoginEnabled}
          appSettings={appSettings}
          biometricEnabled={biometricEnabled}
          handleBiometricLogin={handleBiometricLogin}
          connectWhatsApp={connectWhatsApp}
          skipWhatsApp={skipWhatsApp}
          userProfile={userProfile}
          setUserProfile={setUserProfile}
          handleProfilePicUpload={handleProfilePicUpload}
          searchAddress={searchAddress}
          addressSuggestions={addressSuggestions}
          showAddressSuggestions={showAddressSuggestions}
          selectAddress={selectAddress}
          setShowTwoFactorSetup={setShowTwoFactorSetup}
          twoFactorEnabled={twoFactorEnabled}
          biometricAvailable={biometricAvailable}
          enableBiometric={enableBiometric}
          handleProfileSubmit={handleProfileSubmit}
          showForgotPassword={() => setShowForgotPassword(true)}
          showSignUp={() => setShowSignUp(true)}
          onShowAbout={() => setShowAboutPage(true)}
          error={error}
          setError={setError}
          mobileNotFound={mobileNotFound}
          setMobileNotFound={setMobileNotFound}
        />
      )}

      {!isAuthenticated && showSignUp && activeTab !== 'terms' && activeTab !== 'privacy' && (
        <SignUpModal
          isOpen={showSignUp}
          onClose={() => setShowSignUp(false)}
          onBackToLogin={() => setShowSignUp(false)}
          isAuthenticated={isAuthenticated}
          setIsAuthenticated={setIsAuthenticated}
          authStep={authStep}
          setAuthStep={setAuthStep}
          onShowAbout={() => setShowAboutPage(true)}
          onRegistrationSuccess={onRegistrationSuccess}
        />
      )}

      {!isAuthenticated && showForgotPassword && activeTab !== 'terms' && activeTab !== 'privacy' && (
        <ForgotPasswordModal
          isOpen={showForgotPassword}
          onClose={() => setShowForgotPassword(false)}
          isAuthenticated={isAuthenticated}
          setIsAuthenticated={setIsAuthenticated}
          authStep={authStep}
          setAuthStep={setAuthStep}
          onBackToLogin={() => setShowForgotPassword(false)}
          showToast={showToast}
        />
      )}
      {showAboutPage && (
        <div className="fixed inset-0 z-[200] bg-white overflow-y-auto">
          <div className={isDesktop ? 'w-full max-w-3xl mx-auto px-4 pt-4' : ''}>
            <AboutUsScreen
              onBack={() => setShowAboutPage(false)}
              appSettings={appSettings}
              setActiveTab={setActiveTab}
              appTheme={appTheme}
              isDesktop={isDesktop}
            />
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

const App = () => {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  // Only handle dashboard route, otherwise render main app
  if (normalizedPath === '/dashboard') {
    return <Dashboard />;
  }

  if (normalizedPath === '/admin/pending-wallet-transactions' || normalizedPath === '/admin/pending-wallet-transactions/') {
    return <PendingWalletTransactions />;
  }

  return <MilkEcommerceApp />;
};

export default App;
